import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Building2,
  ClipboardList,
  Inbox,
  Factory,
  Warehouse,
  CheckCircle2,
  Clock,
  Search,
  X,
  Download,
  FileText,
  RefreshCw,
  Edit2,
  Package,
  Calendar,
  AlertCircle,
  Play,
  Pause,
  Layers,
  ArrowRight,
  Send,
  User,
  UserCheck
} from "lucide-react";
import { toast } from "sonner";
import ProductionResources from "./ProductionResources";
import Pagination from "./Pagination";
import { api, downloadDocument } from "../../lib/api";
import { money, compactMoney, getTodayDateString } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import { useSavedState } from "../../lib/listState";

export default function Production({ orders, onOpen, user, onChange, routeParams }) {
  const [tab, setTab] = useSavedState("production", "tab", routeParams?.tab || "requests"); // "requests" | "orders" | "resources"
  const [companyFilter, setCompanyFilter] = useSavedState(
    "production",
    "company",
    routeParams?.company && routeParams.company !== "All Companies" ? routeParams.company : ""
  );

  useEffect(() => {
    if (routeParams?.tab) {
      setTab(routeParams.tab);
    }
    if (routeParams?.company !== undefined) {
      setCompanyFilter(routeParams.company !== "All Companies" ? routeParams.company : "");
    }
  }, [routeParams?.tab, routeParams?.company, setTab, setCompanyFilter]);

  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestSearch, setRequestSearch] = useSavedState("production", "req_search", "");
  const [statusFilter, setStatusFilter] = useSavedState("production", "req_status", "");
  const [reqPriorityFilter, setReqPriorityFilter] = useSavedState("production", "req_priority", "");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedBatchRequest, setSelectedBatchRequest] = useState(null);

  // Orders Tab filters
  const [orderSearch, setOrderSearch] = useSavedState("production", "order_search", "");
  const [prodStatusFilter, setProdStatusFilter] = useSavedState("production", "order_status", "");
  const [prodPriorityFilter, setProdPriorityFilter] = useSavedState("production", "order_priority", "");
  const [prodVendorFilter, setProdVendorFilter] = useSavedState("production", "order_vendor", "");
  const [prodWarehouseFilter, setProdWarehouseFilter] = useSavedState("production", "order_warehouse", "");
  const [prodUserFilter, setProdUserFilter] = useSavedState("production", "order_user", "");
  const [assignUserModalData, setAssignUserModalData] = useState(null);

  // Resource lists
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [productionUsers, setProductionUsers] = useState([]);

  // Batch action modals in orders tab
  const [batchAssignModalData, setBatchAssignModalData] = useState(null);
  const [batchStartModalData, setBatchStartModalData] = useState(null);
  const [batchHoldModalData, setBatchHoldModalData] = useState(null);
  const [batchResumeModalData, setBatchResumeModalData] = useState(null);
  const [batchCompleteModalData, setBatchCompleteModalData] = useState(null);

  // Quick Start & Bulk Actions State
  const [busyOrderIds, setBusyOrderIds] = useState(new Set());
  const [optimisticStatus, setOptimisticStatus] = useState({});
  const [quickStartModalOrder, setQuickStartModalOrder] = useState(null);
  const [holdModalOrder, setHoldModalOrder] = useState(null);
  const [completeModalOrder, setCompleteModalOrder] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkStartModalData, setBulkStartModalData] = useState(null);

  // Pagination for requests
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Pagination for orders
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPageSize, setOrdersPageSize] = useState(10);

  const canAct = isAdmin(user) || canAccessDepartment(user, ["Production"]);
  const canSeePhone = canViewClientPhone(user);

  // Quick Start Single Order Handler (moves Not Started -> In Progress, NEVER Completed)
  const handleQuickStartOrder = useCallback(
    async (order) => {
      if (!order || busyOrderIds.has(order.order_id)) return;

      const hasVendor = Boolean(order.production_vendor_id || order.production_vendor_name || order.assigned_producer);
      const hasWarehouse = Boolean(order.warehouse_id || order.warehouse_name);

      // If missing vendor and warehouse, open modal to explain & assign
      if (!hasVendor && !hasWarehouse) {
        setQuickStartModalOrder(order);
        return;
      }

      setBusyOrderIds((prev) => new Set(prev).add(order.order_id));
      const todayStr = getTodayDateString();

      try {
        await api("patch", `/orders/${order.order_id}/production`, {
          status: "In Progress",
          start_date: todayStr,
          remarks: "Production started directly from order list",
        });
        toast.success(`Production started for #${order.order_id} (Status: In Progress)`);
        setOptimisticStatus((prev) => ({
          ...prev,
          [order.order_id]: {
            production_status: "In Progress",
            production_start_date: todayStr,
            current_stage: "Production",
          },
        }));
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not start production";
        toast.error(typeof msg === "string" ? msg : "Start failed");
        if (typeof msg === "string" && msg.toLowerCase().includes("vendor or warehouse")) {
          setQuickStartModalOrder(order);
        }
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(order.order_id);
          return next;
        });
      }
    },
    [busyOrderIds, onChange]
  );

  // Open Hold Modal (In Progress -> On Hold)
  const handleOpenHoldModal = useCallback((order) => {
    if (!order || busyOrderIds.has(order.order_id)) return;
    setHoldModalOrder(order);
  }, [busyOrderIds]);

  // Confirm Put On Hold Handler
  const handleConfirmPutOnHold = useCallback(
    async ({ orderId, holdReason, nextEstDate }) => {
      if (!orderId || !holdReason?.trim()) {
        toast.error("Hold reason is required");
        return;
      }
      setBusyOrderIds((prev) => new Set(prev).add(orderId));
      try {
        await api("patch", `/orders/${orderId}/production`, {
          status: "On Hold",
          hold_reason: holdReason.trim(),
          remarks: holdReason.trim(),
          expected_completion: nextEstDate || undefined,
          estimated_ready_date: nextEstDate || undefined,
        });
        toast.success(`Order #${orderId} put On Hold`);
        setOptimisticStatus((prev) => ({
          ...prev,
          [orderId]: {
            production_status: "On Hold",
            hold_reason: holdReason.trim(),
            estimated_ready_date: nextEstDate || prev[orderId]?.estimated_ready_date,
            current_stage: "Production",
          },
        }));
        setHoldModalOrder(null);
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not put order on hold";
        toast.error(typeof msg === "string" ? msg : "Hold action failed");
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
      }
    },
    [onChange]
  );

  // Open Complete Modal (In Progress or On Hold -> Completed)
  const handleOpenCompleteModal = useCallback((order) => {
    if (!order || busyOrderIds.has(order.order_id)) return;
    setCompleteModalOrder(order);
  }, [busyOrderIds]);

  // Confirm Complete Handler (marks production as Completed, remains in Production queue)
  const handleConfirmComplete = useCallback(
    async ({ orderId, actualDate, remarks }) => {
      if (!orderId) return;
      setBusyOrderIds((prev) => new Set(prev).add(orderId));
      const todayStr = actualDate || getTodayDateString();
      try {
        await api("patch", `/orders/${orderId}/production`, {
          status: "Completed",
          actual_completion: todayStr,
          remarks: remarks || "Production completed directly from order list",
          handover_to_dispatch: false,
        });
        toast.success(`Production completed for #${orderId}`);
        setOptimisticStatus((prev) => ({
          ...prev,
          [orderId]: {
            ...prev[orderId],
            production_status: "Completed",
            current_stage: "Production",
            handed_to_dispatch: false,
            production_actual: todayStr,
          },
        }));
        setCompleteModalOrder(null);
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not complete production";
        toast.error(typeof msg === "string" ? msg : "Complete failed");
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
      }
    },
    [onChange]
  );

  // Ready for Dispatch Handler (hands over completed order/product to Dispatch queue)
  const handleReadyForDispatch = useCallback(
    async (orderOrId) => {
      const orderId = typeof orderOrId === "string" ? orderOrId : orderOrId?.order_id;
      if (!orderId || busyOrderIds.has(orderId)) return;

      setBusyOrderIds((prev) => new Set(prev).add(orderId));
      const todayStr = getTodayDateString();

      try {
        await api("post", `/orders/${orderId}/send-to-dispatch`);
        toast.success(`Order #${orderId} sent to Dispatch (Ready for Dispatch)`);
        setOptimisticStatus((prev) => ({
          ...prev,
          [orderId]: {
            ...prev[orderId],
            production_status: "Completed",
            current_stage: "Dispatch",
            dispatch_status: "Ready for Dispatch",
            ready_for_dispatch_at: new Date().toISOString(),
            production_actual: prev[orderId]?.production_actual || todayStr,
            handed_to_dispatch: true,
          },
        }));
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not send order to dispatch";
        toast.error(typeof msg === "string" ? msg : "Dispatch handover failed");
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
      }
    },
    [busyOrderIds, onChange]
  );

  // Batch Ready for Dispatch Handler (hands over completed products in a bulk batch to Dispatch queue)
  const handleBatchReadyForDispatch = useCallback(
    async (batchId, orderIds) => {
      if (!batchId || busyOrderIds.has(batchId)) return;
      setBusyOrderIds((prev) => new Set(prev).add(batchId));
      const todayStr = getTodayDateString();

      try {
        await api("post", `/orders/batch/${batchId}/send-to-dispatch`, {
          order_ids: orderIds && orderIds.length > 0 ? orderIds : null,
        });
        toast.success(`Sent completed products in batch #${batchId} to Dispatch`);
        setOptimisticStatus((prev) => {
          const next = { ...prev };
          (orderIds || []).forEach((oid) => {
            next[oid] = {
              ...next[oid],
              production_status: "Completed",
              current_stage: "Dispatch",
              dispatch_status: "Ready for Dispatch",
              ready_for_dispatch_at: new Date().toISOString(),
              production_actual: prev[oid]?.production_actual || todayStr,
              handed_to_dispatch: true,
            };
          });
          return next;
        });
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not send batch products to dispatch";
        toast.error(typeof msg === "string" ? msg : "Batch dispatch handover failed");
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(batchId);
          return next;
        });
      }
    },
    [busyOrderIds, onChange]
  );

  // Batch Hold Production Handler (puts In Progress products on hold)
  const handleBatchHold = useCallback(
    async ({ batchId, orderIds, holdReason, nextEstDate }) => {
      if (!batchId || !orderIds || orderIds.length === 0) return;
      if (!holdReason?.trim()) {
        toast.error("Hold reason is required");
        return;
      }

      setBusyOrderIds((prev) => new Set(prev).add(batchId));
      try {
        await api("patch", `/orders/batch/${batchId}/production`, {
          order_ids: orderIds,
          status: "On Hold",
          hold_reason: holdReason.trim(),
          remarks: holdReason.trim(),
          expected_completion: nextEstDate || undefined,
          estimated_ready_date: nextEstDate || undefined,
        });

        toast.success(`Put ${orderIds.length} product(s) on hold in batch #${batchId}`);
        setOptimisticStatus((prev) => {
          const next = { ...prev };
          orderIds.forEach((oid) => {
            next[oid] = {
              ...next[oid],
              production_status: "On Hold",
              hold_reason: holdReason.trim(),
              estimated_ready_date: nextEstDate || next[oid]?.estimated_ready_date,
              current_stage: "Production",
            };
          });
          return next;
        });
        setBatchHoldModalData(null);
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not put batch products on hold";
        toast.error(typeof msg === "string" ? msg : "Bulk hold failed");
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(batchId);
          return next;
        });
      }
    },
    [onChange]
  );

  // Batch Resume Production Handler (moves On Hold -> In Progress)
  const handleBatchResume = useCallback(
    async ({ batchId, orderIds }) => {
      if (!batchId || !orderIds || orderIds.length === 0) return;

      setBusyOrderIds((prev) => new Set(prev).add(batchId));
      try {
        await api("patch", `/orders/batch/${batchId}/production`, {
          order_ids: orderIds,
          status: "In Progress",
          hold_reason: "",
          remarks: "Production resumed via bulk batch action",
        });

        toast.success(`Resumed production for ${orderIds.length} product(s) in batch #${batchId}`);
        setOptimisticStatus((prev) => {
          const next = { ...prev };
          orderIds.forEach((oid) => {
            next[oid] = {
              ...next[oid],
              production_status: "In Progress",
              hold_reason: "",
              current_stage: "Production",
            };
          });
          return next;
        });
        setBatchResumeModalData(null);
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not resume batch production";
        toast.error(typeof msg === "string" ? msg : "Bulk resume failed");
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(batchId);
          return next;
        });
      }
    },
    [onChange]
  );

  // Batch Complete Production Handler (marks In Progress products as Completed)
  const handleBatchComplete = useCallback(
    async ({ batchId, orderIds, actualDate, remarks }) => {
      if (!batchId || !orderIds || orderIds.length === 0) return;

      setBusyOrderIds((prev) => new Set(prev).add(batchId));
      const todayStr = actualDate || getTodayDateString();
      try {
        await api("patch", `/orders/batch/${batchId}/production`, {
          order_ids: orderIds,
          status: "Completed",
          actual_completion: todayStr,
          remarks: remarks || "Production completed via bulk batch action",
          handover_to_dispatch: false,
        });

        toast.success(`Completed production for ${orderIds.length} product(s) in batch #${batchId}`);
        setOptimisticStatus((prev) => {
          const next = { ...prev };
          orderIds.forEach((oid) => {
            next[oid] = {
              ...next[oid],
              production_status: "Completed",
              current_stage: "Production",
              handed_to_dispatch: false,
              production_actual: todayStr,
            };
          });
          return next;
        });
        setBatchCompleteModalData(null);
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not complete batch production";
        toast.error(typeof msg === "string" ? msg : "Bulk complete failed");
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(batchId);
          return next;
        });
      }
    },
    [onChange]
  );


  // Quick Resume Single Order Handler (moves On Hold -> In Progress)
  const handleQuickResumeOrder = useCallback(
    async (order) => {
      if (!order || busyOrderIds.has(order.order_id)) return;
      setBusyOrderIds((prev) => new Set(prev).add(order.order_id));

      try {
        await api("patch", `/orders/${order.order_id}/production`, {
          status: "In Progress",
          remarks: "Production resumed directly from order list",
        });
        toast.success(`Production resumed for #${order.order_id} (Status: In Progress)`);
        setOptimisticStatus((prev) => ({
          ...prev,
          [order.order_id]: {
            production_status: "In Progress",
            hold_reason: "",
            current_stage: "Production",
          },
        }));
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not resume production";
        toast.error(typeof msg === "string" ? msg : "Resume failed");
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          next.delete(order.order_id);
          return next;
        });
      }
    },
    [busyOrderIds, onChange]
  );

  // Fetch Vendor Requests
  const loadRequests = useCallback(async () => {
    try {
      setLoadingRequests(true);
      const res = await api("get", "/production/vendor-requests");
      setRequests(res.data || []);
    } catch (err) {
      console.error("Failed to load vendor requests:", err);
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  const loadActiveResources = useCallback(() => {
    api("get", "/vendors?status=Active").then((r) => setVendors(r.data || [])).catch(() => {});
    api("get", "/warehouses?status=Active").then((r) => setWarehouses(r.data || [])).catch(() => {});
    api("get", "/users?active_only=true").then((r) => {
      const allActive = r.data || [];
      const prodEligible = allActive.filter(
        (u) =>
          !u.is_deleted &&
          u.is_active !== false &&
          (u.role === "admin" ||
            u.role === "super_admin" ||
            (u.department || "").toLowerCase() === "production" ||
            (u.department || "").toLowerCase() === "management" ||
            (u.departments || []).some((d) => ["production", "management"].includes(d.toLowerCase())))
      );
      setProductionUsers(prodEligible);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadRequests();
    loadActiveResources();
  }, [loadRequests, loadActiveResources]);

  const warehouseMap = useMemo(() => {
    const map = {};
    (warehouses || []).forEach((w) => {
      if (w.warehouse_id) map[w.warehouse_id] = w;
      if (w.name) map[w.name.toLowerCase().trim()] = w;
      if (w.warehouse_name) map[w.warehouse_name.toLowerCase().trim()] = w;
    });
    return map;
  }, [warehouses]);

  const getWarehouseUsersForOrder = useCallback((o) => {
    if (o?.warehouse_assigned_user_names && o.warehouse_assigned_user_names.length > 0) {
      return o.warehouse_assigned_user_names;
    }
    const key = o?.warehouse_id || (o?.warehouse_name || "").toLowerCase().trim();
    const wh = warehouseMap[key];
    return wh?.assigned_user_names || [];
  }, [warehouseMap]);

  // When switching tabs to requests, orders, or resources, refresh active resources
  useEffect(() => {
    loadActiveResources();
  }, [tab, loadActiveResources]);

  // Calculate warehouses assigned to current production user
  const userAssignedWarehouses = useMemo(() => {
    if (!user) return [];
    const role = (user.role || "").toLowerCase().trim();
    if (role === "admin" || role === "super_admin") return [];
    const uId = user.user_id || "";
    const uName = (user.name || "").trim().toLowerCase();

    return (warehouses || []).filter((w) => {
      const uIds = (w.assigned_user_ids || []).map((id) => String(id));
      const uNames = (w.assigned_user_names || []).map((n) => String(n).trim().toLowerCase());
      const prodUsers = w.assigned_production_users || [];
      const hasId = uId && (uIds.includes(uId) || prodUsers.some((u) => u?.user_id === uId));
      const hasName = uName && (uNames.includes(uName) || prodUsers.some((u) => (u?.name || "").trim().toLowerCase() === uName));
      return Boolean(hasId || hasName);
    });
  }, [user, warehouses]);

  const visibleWarehouseOptions = useMemo(() => {
    if (userAssignedWarehouses.length > 0) {
      return userAssignedWarehouses;
    }
    return warehouses;
  }, [userAssignedWarehouses, warehouses]);

  // Production Orders List with optimistic instantaneous status updates
  const rawProductionOrders = useMemo(() => {
    const hasWhAssignment = userAssignedWarehouses.length > 0;
    const allowedWhIds = hasWhAssignment ? new Set(userAssignedWarehouses.map((w) => w.warehouse_id).filter(Boolean)) : null;
    const allowedWhNames = hasWhAssignment ? new Set(userAssignedWarehouses.map((w) => (w.name || w.warehouse_name || "").trim().toLowerCase()).filter(Boolean)) : null;

    return (orders || []).map((o) => {
      if (optimisticStatus[o.order_id]) {
        return { ...o, ...optimisticStatus[o.order_id] };
      }
      return o;
    }).filter((o) => {
      if (o.deleted) return false;
      if (o.current_stage !== "Production") return false;
      if (o.handed_to_dispatch === true) return false;
      if (o.dispatch_status && o.dispatch_status !== "Pending") return false;
      if (companyFilter) {
        const oComp = (o.company || "").toLowerCase();
        if (oComp && oComp !== companyFilter.toLowerCase()) return false;
      }
      if (hasWhAssignment) {
        const oWhId = o.warehouse_id;
        const oWhName = (o.warehouse_name || "").trim().toLowerCase();
        const matches = (oWhId && allowedWhIds.has(oWhId)) || (oWhName && allowedWhNames.has(oWhName));
        if (!matches) return false;
      }
      return true;
    });
  }, [orders, optimisticStatus, companyFilter, userAssignedWarehouses]);

  // Group production orders into standalone single orders and grouped bulk batches
  const groupedProductionItems = useMemo(() => {
    const batchMap = new Map();
    const singles = [];

    rawProductionOrders.forEach((o) => {
      const batchId = o.upload_batch_id;
      if (batchId) {
        if (!batchMap.has(batchId)) {
          batchMap.set(batchId, []);
        }
        batchMap.get(batchId).push(o);
      } else {
        singles.push(o);
      }
    });

    const items = [...singles];

    batchMap.forEach((batchOrders, batchId) => {
      if (batchOrders.length === 1 && !batchOrders[0].is_bulk_upload) {
        items.push(batchOrders[0]);
        return;
      }

      const first = batchOrders[0];
      const counts = {
        total: batchOrders.length,
        "Not Started": batchOrders.filter((o) => (o.production_status || "Not Started") === "Not Started").length,
        "In Progress": batchOrders.filter((o) => o.production_status === "In Progress").length,
        "On Hold": batchOrders.filter((o) => o.production_status === "On Hold").length,
        Completed: batchOrders.filter((o) => o.production_status === "Completed").length,
      };

      const priority = batchOrders.some((o) => o.priority === "Urgent")
        ? "Urgent"
        : batchOrders.some((o) => o.priority === "High")
        ? "High"
        : "Normal";

      items.push({
        isBatch: true,
        batch_id: batchId,
        batch_name: first.upload_batch_name || `Bulk Production Request · ${batchOrders.length} Orders`,
        client_name: first.client_name,
        client_id: first.client_id,
        client_contact_person: first.client_contact_person,
        client_phone: first.client_phone,
        client_email: first.client_email,
        priority,
        total_value: batchOrders.reduce((sum, o) => sum + (o.total_value || 0), 0),
        orders: batchOrders,
        counts,
        created_at: first.created_at,
      });
    });

    // Always sort newest first
    items.sort((a, b) => {
      const getT = (item) => {
        if (item.isBatch) {
          const times = (item.orders || []).map((o) => {
            const t = o.created_at || o.production_start_date || o.updated_at;
            return t ? new Date(t).getTime() : 0;
          });
          return times.length ? Math.max(...times) : 0;
        }
        const t = item.created_at || item.production_start_date || item.updated_at;
        return t ? new Date(t).getTime() : 0;
      };
      return getT(b) - getT(a);
    });

    return items;
  }, [rawProductionOrders]);

  // Production Metrics (Top-level Production Requests / groups count)
  const productionMetrics = useMemo(() => {
    let notStarted = 0;
    let inProgress = 0;
    let onHold = 0;
    let completed = 0;

    groupedProductionItems.forEach((item) => {
      if (item.isBatch) {
        const c = item.counts || {};
        if (c.Completed === c.total && c.total > 0) {
          completed += 1;
        } else if (c["On Hold"] > 0) {
          onHold += 1;
        } else if (c["In Progress"] > 0) {
          inProgress += 1;
        } else {
          notStarted += 1;
        }
      } else {
        const st = item.production_status || "Not Started";
        if (st === "Completed") completed += 1;
        else if (st === "On Hold") onHold += 1;
        else if (st === "In Progress") inProgress += 1;
        else notStarted += 1;
      }
    });

    return {
      total: groupedProductionItems.length,
      notStarted,
      inProgress,
      onHold,
      completed,
    };
  }, [groupedProductionItems]);

  // Filtered Production Items
  const filteredProductionItems = useMemo(() => {
    return groupedProductionItems.filter((item) => {
      if (item.isBatch) {
        if (prodStatusFilter) {
          const hasStatus = item.orders.some((o) => (o.production_status || "Not Started") === prodStatusFilter);
          if (!hasStatus) return false;
        }
        if (prodPriorityFilter && item.priority !== prodPriorityFilter) return false;
        if (prodVendorFilter) {
          if (prodVendorFilter === "unassigned") {
            const hasUnassigned = item.orders.some((o) => !o.production_vendor_name);
            if (!hasUnassigned) return false;
          } else {
            const hasVendor = item.orders.some(
              (o) => o.production_vendor_name === prodVendorFilter
            );
            if (!hasVendor) return false;
          }
        }
        if (prodUserFilter) {
          if (prodUserFilter === "unassigned") {
            const hasUnassigned = item.orders.some((o) => !o.assigned_producer && !o.assigned_production_user);
            if (!hasUnassigned) return false;
          } else {
            const hasUser = item.orders.some(
              (o) => (o.assigned_producer || o.assigned_production_user) === prodUserFilter
            );
            if (!hasUser) return false;
          }
        }
        if (prodWarehouseFilter) {
          if (prodWarehouseFilter === "unassigned") {
            const hasUnassigned = item.orders.some((o) => !o.warehouse_name);
            if (!hasUnassigned) return false;
          } else {
            const hasWarehouse = item.orders.some((o) => o.warehouse_name === prodWarehouseFilter);
            if (!hasWarehouse) return false;
          }
        }
        if (orderSearch) {
          const q = orderSearch.toLowerCase();
          const match =
            item.batch_id.toLowerCase().includes(q) ||
            item.batch_name.toLowerCase().includes(q) ||
            item.client_name.toLowerCase().includes(q) ||
            item.client_contact_person?.toLowerCase().includes(q) ||
            item.orders.some(
              (o) =>
                o.order_id.toLowerCase().includes(q) ||
                o.product.toLowerCase().includes(q) ||
                o.production_vendor_name?.toLowerCase().includes(q) ||
                o.assigned_producer?.toLowerCase().includes(q) ||
                o.warehouse_name?.toLowerCase().includes(q) ||
                (getWarehouseUsersForOrder(o) || []).some((u) => u.toLowerCase().includes(q)) ||
                o.hold_reason?.toLowerCase().includes(q) ||
                o.production_remarks?.toLowerCase().includes(q)
            );
          if (!match) return false;
        }
        return true;
      }

      // Standalone single order
      const st = item.production_status || "Not Started";
      if (prodStatusFilter && st !== prodStatusFilter) return false;
      if (prodPriorityFilter && item.priority !== prodPriorityFilter) return false;
      if (prodVendorFilter) {
        const vName = item.production_vendor_name;
        if (prodVendorFilter === "unassigned") {
          if (vName) return false;
        } else if (vName !== prodVendorFilter) {
          return false;
        }
      }
      if (prodUserFilter) {
        const uName = item.assigned_producer || item.assigned_production_user;
        if (prodUserFilter === "unassigned") {
          if (uName) return false;
        } else if (uName !== prodUserFilter) {
          return false;
        }
      }
      if (prodWarehouseFilter) {
        if (prodWarehouseFilter === "unassigned") {
          if (item.warehouse_name) return false;
        } else if (item.warehouse_name !== prodWarehouseFilter) {
          return false;
        }
      }
      if (orderSearch) {
        const q = orderSearch.toLowerCase();
        const match =
          item.order_id?.toLowerCase().includes(q) ||
          item.client_name?.toLowerCase().includes(q) ||
          item.client_contact_person?.toLowerCase().includes(q) ||
          item.product?.toLowerCase().includes(q) ||
          item.production_vendor_name?.toLowerCase().includes(q) ||
          item.assigned_producer?.toLowerCase().includes(q) ||
          item.warehouse_name?.toLowerCase().includes(q) ||
          (getWarehouseUsersForOrder(item) || []).some((u) => u.toLowerCase().includes(q)) ||
          item.hold_reason?.toLowerCase().includes(q) ||
          item.production_remarks?.toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [groupedProductionItems, prodStatusFilter, prodPriorityFilter, prodVendorFilter, prodUserFilter, prodWarehouseFilter, orderSearch, getWarehouseUsersForOrder]);

  const paginatedProductionItems = useMemo(() => {
    const start = (ordersPage - 1) * ordersPageSize;
    return filteredProductionItems.slice(start, start + ordersPageSize);
  }, [filteredProductionItems, ordersPage, ordersPageSize]);

  // Order IDs on current page eligible for quick start (Not Started standalone single orders)
  const eligiblePageOrderIds = useMemo(() => {
    return paginatedProductionItems
      .filter((it) => !it.isBatch && (it.production_status || "Not Started") === "Not Started")
      .map((it) => it.order_id);
  }, [paginatedProductionItems]);

  // Group Vendor Requests by batch
  const groupedVendorRequests = useMemo(() => {
    const batchMap = new Map();
    const singles = [];

    requests.forEach((r) => {
      const bId = r.upload_batch_id;
      if (bId) {
        if (!batchMap.has(bId)) {
          batchMap.set(bId, []);
        }
        batchMap.get(bId).push(r);
      } else {
        singles.push(r);
      }
    });

    const items = [...singles];

    batchMap.forEach((bReqs, bId) => {
      if (bReqs.length === 1 && !bReqs[0].is_bulk_upload) {
        items.push(bReqs[0]);
        return;
      }

      const first = bReqs[0];
      const total = bReqs.length;
      const fullyConfirmedCount = bReqs.filter(
        (r) => Boolean(r.vendor_name || r.production_vendor_name || r.vendor_id || r.warehouse_name || r.warehouse_id)
      ).length;
      const pendingCount = total - fullyConfirmedCount;
      const isAllConfirmed = total > 0 && fullyConfirmedCount === total;

      const priority = bReqs.some((r) => r.priority === "Urgent")
        ? "Urgent"
        : bReqs.some((r) => r.priority === "High")
        ? "High"
        : "Normal";

      // Find latest timestamp across all requests in this batch
      const batchTimestamps = bReqs.map((r) => {
        const t = r.requested_at || r.created_at || r.updated_at;
        return t ? new Date(t).getTime() : 0;
      });
      const latestTimestamp = batchTimestamps.length ? Math.max(...batchTimestamps) : 0;
      const latestReq = bReqs.find((r) => {
        const t = r.requested_at || r.created_at || r.updated_at;
        return t && new Date(t).getTime() === latestTimestamp;
      }) || first;

      items.push({
        isBatch: true,
        batch_id: bId,
        batch_name: first.upload_batch_name || `Bulk Production Request · ${bReqs.length} Products`,
        client_name: first.client_name,
        client_id: first.client_id,
        client_contact_person: first.client_contact_person,
        client_phone: first.client_phone,
        client_email: first.client_email,
        priority,
        total_value: bReqs.reduce((sum, r) => sum + (r.total_value || 0), 0),
        requests: bReqs,
        pendingCount,
        confirmedCount: fullyConfirmedCount,
        isAllConfirmed,
        requested_at: latestReq.requested_at || first.requested_at,
        created_at: latestReq.created_at || first.created_at,
        requested_by: latestReq.requested_by || first.requested_by,
        latest_timestamp: latestTimestamp,
      });
    });

    // Always sort newest Vendor Requests first
    items.sort((a, b) => {
      const getT = (item) => {
        if (item.latest_timestamp) return item.latest_timestamp;
        const t = item.requested_at || item.created_at || item.updated_at;
        return t ? new Date(t).getTime() : 0;
      };
      return getT(b) - getT(a);
    });

    return items;
  }, [requests]);

  // Filtered vendor requests
  const filteredRequests = useMemo(() => {
    return groupedVendorRequests.filter((item) => {
      if (item.isBatch) {
        if (statusFilter) {
          if (statusFilter === "pending" && item.isAllConfirmed) return false;
          if (statusFilter === "confirmed" && !item.isAllConfirmed) return false;
        }
        if (reqPriorityFilter && item.priority !== reqPriorityFilter) return false;
        if (requestSearch) {
          const q = requestSearch.toLowerCase();
          const match =
            item.batch_id.toLowerCase().includes(q) ||
            item.batch_name.toLowerCase().includes(q) ||
            item.client_name.toLowerCase().includes(q) ||
            item.client_contact_person?.toLowerCase().includes(q) ||
            item.requests.some(
              (r) =>
                r.order_id.toLowerCase().includes(q) ||
                r.product.toLowerCase().includes(q) ||
                r.vendor_name?.toLowerCase().includes(q) ||
                r.warehouse_name?.toLowerCase().includes(q) ||
                r.requested_by?.toLowerCase().includes(q)
            );
          if (!match) return false;
        }
        return true;
      }

      // Standalone single request
      const isSingleConfirmed = Boolean(item.vendor_name || item.production_vendor_name || item.vendor_id || item.warehouse_name || item.warehouse_id);
      if (statusFilter) {
        if (statusFilter === "pending" && isSingleConfirmed) return false;
        if (statusFilter === "confirmed" && !isSingleConfirmed) return false;
      }
      if (reqPriorityFilter && item.priority !== reqPriorityFilter) return false;
      if (requestSearch) {
        const q = requestSearch.toLowerCase();
        const matchStr = `${item.order_id || ""} ${item.client_name || ""} ${item.product || ""} ${item.vendor_name || ""} ${item.warehouse_name || ""} ${item.requested_by || ""}`.toLowerCase();
        if (!matchStr.includes(q)) return false;
      }
      return true;
    });
  }, [groupedVendorRequests, statusFilter, reqPriorityFilter, requestSearch]);

  const paginatedRequests = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRequests.slice(start, start + pageSize);
  }, [filteredRequests, page, pageSize]);

  const pendingRequestsCount = useMemo(() => {
    return requests.filter((r) => {
      const hasV = Boolean(r.vendor_name || r.production_vendor_name || r.vendor_id);
      const hasW = Boolean(r.warehouse_name || r.warehouse_id);
      return !hasV && !hasW;
    }).length;
  }, [requests]);

  const requestMetrics = useMemo(() => {
    let pending = 0;
    let confirmed = 0;
    groupedVendorRequests.forEach((item) => {
      if (item.isBatch) {
        if (item.isAllConfirmed) {
          confirmed += 1;
        } else {
          pending += 1;
        }
      } else {
        const hasV = Boolean(item.vendor_name || item.production_vendor_name || item.vendor_id);
        const hasW = Boolean(item.warehouse_name || item.warehouse_id);
        if (hasV || hasW) {
          confirmed += 1;
        } else {
          pending += 1;
        }
      }
    });
    return {
      total: groupedVendorRequests.length,
      pending,
      confirmed,
    };
  }, [groupedVendorRequests]);

  const segmentedNav = (
    <div
      style={{
        display: "inline-flex",
        background: "#f1f5f9",
        padding: "3px",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
      }}
    >
      {/* Vendor Requests Tab */}
      <button
        type="button"
        onClick={() => setTab("requests")}
        data-testid="production-nav-requests"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 14px",
          borderRadius: "6px",
          fontSize: "12.5px",
          fontWeight: tab === "requests" ? 700 : 500,
          background: tab === "requests" ? "#ffffff" : "transparent",
          color: tab === "requests" ? "var(--navy)" : "#64748b",
          border: 0,
          boxShadow: tab === "requests" ? "0 1px 3px rgba(15,23,42,0.12)" : "none",
          cursor: "pointer",
          transition: "all 0.15s ease",
        }}
      >
        <Inbox size={14} />
        <span>Vendor Requests</span>
        <span
          style={{
            fontSize: "11px",
            padding: "1px 7px",
            borderRadius: "10px",
            fontWeight: 700,
            background: pendingRequestsCount > 0 ? "#fef3c7" : (tab === "requests" ? "#eff6ff" : "#e2e8f0"),
            color: pendingRequestsCount > 0 ? "#b45309" : (tab === "requests" ? "#2563eb" : "#475569"),
          }}
        >
          {pendingRequestsCount}
        </span>
      </button>

      {/* Orders in Production Tab */}
      <button
        type="button"
        onClick={() => setTab("orders")}
        data-testid="production-nav-orders"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 14px",
          borderRadius: "6px",
          fontSize: "12.5px",
          fontWeight: tab === "orders" ? 700 : 500,
          background: tab === "orders" ? "#ffffff" : "transparent",
          color: tab === "orders" ? "var(--navy)" : "#64748b",
          border: 0,
          boxShadow: tab === "orders" ? "0 1px 3px rgba(15,23,42,0.12)" : "none",
          cursor: "pointer",
          transition: "all 0.15s ease",
        }}
      >
        <ClipboardList size={14} />
        <span>Orders in Production</span>
        <span
          style={{
            fontSize: "11px",
            padding: "1px 7px",
            borderRadius: "10px",
            fontWeight: 700,
            background: tab === "orders" ? "#eff6ff" : "#e2e8f0",
            color: tab === "orders" ? "#2563eb" : "#475569",
          }}
        >
          {groupedProductionItems.length}
        </span>
      </button>

      {/* Production Resources Tab */}
      <button
        type="button"
        onClick={() => setTab("resources")}
        data-testid="production-nav-resources"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 14px",
          borderRadius: "6px",
          fontSize: "12.5px",
          fontWeight: tab === "resources" ? 700 : 500,
          background: tab === "resources" ? "#ffffff" : "transparent",
          color: tab === "resources" ? "var(--navy)" : "#64748b",
          border: 0,
          boxShadow: tab === "resources" ? "0 1px 3px rgba(15,23,42,0.12)" : "none",
          cursor: "pointer",
          transition: "all 0.15s ease",
        }}
      >
        <Building2 size={14} />
        <span>Production Resources</span>
      </button>
    </div>
  );

  // Handler for saving internal Production User assignment
  const handleSaveProductionUser = useCallback(
    async ({ mode, order, batch, targetOrders, assignedUser, assignedUserId, remarks }) => {
      const targetIds = (targetOrders || (order ? [order] : [])).map((o) => o.order_id);
      setBusyOrderIds((prev) => new Set([...prev, ...targetIds]));
      try {
        if (mode === "batch" && batch) {
          await api("patch", `/orders/batch/${batch.batch_id}/assign-production-user`, {
            order_ids: targetIds,
            assigned_user: assignedUser,
            assigned_user_id: assignedUserId,
            remarks: remarks || "Batch assigned production user",
          });
          toast.success(`Assigned ${assignedUser || "Unassigned"} across batch #${batch.batch_id}`);
        } else if (order) {
          await api("patch", `/orders/${order.order_id}/assign-production-user`, {
            assigned_user: assignedUser,
            assigned_user_id: assignedUserId,
            remarks: remarks || "Assigned production user",
          });
          toast.success(`Assigned ${assignedUser || "Unassigned"} to #${order.order_id}`);
        }

        setOptimisticStatus((prev) => {
          const next = { ...prev };
          targetIds.forEach((oid) => {
            next[oid] = {
              ...next[oid],
              assigned_producer: assignedUser,
              producer_id: assignedUserId,
              assigned_production_user: assignedUser,
              production_user_id: assignedUserId,
            };
          });
          return next;
        });

        setAssignUserModalData(null);
        onChange && onChange();
      } catch (err) {
        const msg = err.response?.data?.detail || "Could not assign production user";
        toast.error(typeof msg === "string" ? msg : "Assignment failed");
      } finally {
        setBusyOrderIds((prev) => {
          const next = new Set(prev);
          targetIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    },
    [onChange]
  );

  return (
    <div className="content" data-testid="production-workspace-view">
      {/* 1. Unified Page Heading */}
      <div className="page-heading" style={{ marginBottom: 12 }}>
        <div>
          <div className="small-label">PRODUCTION</div>
          <h1>Production</h1>
          <p className="muted">
            {tab === "requests" && "Pending vendor and warehouse assignment requests from the Design team."}
            {tab === "orders" && "Shop-floor production orders and grouped bulk production requests."}
            {tab === "resources" && "Manage production vendors and procurement warehouse locations."}
          </p>
        </div>
      </div>

      {/* 2. Unified Tab Navigation - Directly below heading and subtitle */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        {segmentedNav}
        {tab === "requests" && (
          <button
            type="button"
            className="outline-btn compact"
            onClick={loadRequests}
            disabled={loadingRequests}
            title="Refresh requests"
            data-testid="vendor-requests-refresh-btn"
          >
            <RefreshCw size={14} className={loadingRequests ? "spin" : ""} />
            <span>Refresh</span>
          </button>
        )}
      </div>

      {/* 2. TAB CONTENT: PRODUCTION RESOURCES */}
      {tab === "resources" && (
        <ProductionResources user={user} />
      )}

      {/* 3. TAB CONTENT: ORDERS IN PRODUCTION */}
      {tab === "orders" && (
        <div data-testid="orders-in-production-view">
          {companyFilter && (
            <div className="active-filter-banner" style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>Active Filter:</span>
                <span className="badge purple" style={{ fontWeight: 700 }} data-testid="active-filter-production-company">
                  Company: {companyFilter}
                </span>
                <span className="badge gray" style={{ fontWeight: 600 }}>
                  {groupedProductionItems.length} matching {groupedProductionItems.length === 1 ? "order" : "orders"}
                </span>
              </div>
              <button
                type="button"
                className="clear-btn"
                onClick={() => setCompanyFilter("")}
                data-testid="clear-production-filter-btn"
              >
                Clear filter
              </button>
            </div>
          )}

          {/* Summary Metrics (Interactive Filters) */}
          <div className="metrics-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
            <MetricBox
              label="Total in Production"
              value={productionMetrics.total}
              tone="blue"
              active={!prodStatusFilter}
              onClick={() => {
                setProdStatusFilter("");
                setOrdersPage(1);
              }}
            />
            <MetricBox
              label="Not Started"
              value={productionMetrics.notStarted}
              tone="blue"
              active={prodStatusFilter === "Not Started"}
              onClick={() => {
                setProdStatusFilter(prodStatusFilter === "Not Started" ? "" : "Not Started");
                setOrdersPage(1);
              }}
            />
            <MetricBox
              label="In Progress"
              value={productionMetrics.inProgress}
              tone="purple"
              active={prodStatusFilter === "In Progress"}
              onClick={() => {
                setProdStatusFilter(prodStatusFilter === "In Progress" ? "" : "In Progress");
                setOrdersPage(1);
              }}
            />
            <MetricBox
              label="On Hold"
              value={productionMetrics.onHold}
              tone="amber"
              active={prodStatusFilter === "On Hold"}
              onClick={() => {
                setProdStatusFilter(prodStatusFilter === "On Hold" ? "" : "On Hold");
                setOrdersPage(1);
              }}
            />
            <MetricBox
              label="Completed"
              value={productionMetrics.completed}
              tone="green"
              active={prodStatusFilter === "Completed"}
              onClick={() => {
                setProdStatusFilter(prodStatusFilter === "Completed" ? "" : "Completed");
                setOrdersPage(1);
              }}
            />
          </div>

          {/* Search & Filters Toolbar */}
          <div className="toolbar" style={{ marginTop: 14, marginBottom: 16 }}>
            <div className="search-box">
              <Search size={16} />
              <input
                placeholder="Search Order ID, batch ID, client, product, vendor…"
                value={orderSearch}
                onChange={(e) => {
                  setOrderSearch(e.target.value);
                  setOrdersPage(1);
                }}
                data-testid="production-search-input"
              />
              {orderSearch && (
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => {
                    setOrderSearch("");
                    setOrdersPage(1);
                  }}
                  style={{ padding: 2, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <select
              value={prodStatusFilter}
              onChange={(e) => {
                setProdStatusFilter(e.target.value);
                setOrdersPage(1);
              }}
              data-testid="production-filter-status"
            >
              <option value="">All Statuses</option>
              <option value="Not Started">Not Started</option>
              <option value="In Progress">In Progress</option>
              <option value="On Hold">On Hold</option>
              <option value="Completed">Completed</option>
            </select>

            <select
              value={prodPriorityFilter}
              onChange={(e) => {
                setProdPriorityFilter(e.target.value);
                setOrdersPage(1);
              }}
              data-testid="production-filter-priority"
            >
              <option value="">All Priorities</option>
              <option value="Urgent">Urgent</option>
              <option value="High">High</option>
              <option value="Normal">Normal</option>
            </select>

            <select
              value={prodVendorFilter}
              onChange={(e) => {
                setProdVendorFilter(e.target.value);
                setOrdersPage(1);
              }}
              data-testid="production-filter-vendor"
            >
              <option value="">All Production Vendors</option>
              <option value="unassigned">Unassigned</option>
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.name || v.vendor_name}>
                  {v.name || v.vendor_name}
                </option>
              ))}
            </select>

            <select
              value={prodUserFilter}
              onChange={(e) => {
                setProdUserFilter(e.target.value);
                setOrdersPage(1);
              }}
              data-testid="production-filter-user"
            >
              <option value="">All Assigned Users</option>
              <option value="unassigned">Unassigned</option>
              {productionUsers.map((u) => (
                <option key={u.user_id || u.name} value={u.name}>
                  {u.name}
                </option>
              ))}
            </select>

            <select
              value={prodWarehouseFilter}
              onChange={(e) => {
                setProdWarehouseFilter(e.target.value);
                setOrdersPage(1);
              }}
              data-testid="production-filter-warehouse"
            >
              <option value="">All Warehouses</option>
              {userAssignedWarehouses.length === 0 && <option value="unassigned">Unassigned</option>}
              {visibleWarehouseOptions.map((w) => (
                <option key={w.warehouse_id} value={w.name || w.warehouse_name}>
                  {w.name || w.warehouse_name}
                </option>
              ))}
            </select>

            {(orderSearch || prodStatusFilter || prodPriorityFilter || prodVendorFilter || prodUserFilter || prodWarehouseFilter) && (
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => {
                  setOrderSearch("");
                  setProdStatusFilter("");
                  setProdPriorityFilter("");
                  setProdVendorFilter("");
                  setProdUserFilter("");
                  setProdWarehouseFilter("");
                  setOrdersPage(1);
                }}
                style={{ fontSize: 12 }}
                title="Reset all filters"
              >
                Reset Filters
              </button>
            )}

            {canAct && eligiblePageOrderIds.length > 0 && (
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => {
                  const allSelected = eligiblePageOrderIds.every((id) => selectedOrderIds.includes(id));
                  if (allSelected) {
                    setSelectedOrderIds((prev) => prev.filter((id) => !eligiblePageOrderIds.includes(id)));
                  } else {
                    setSelectedOrderIds((prev) => Array.from(new Set([...prev, ...eligiblePageOrderIds])));
                  }
                }}
                style={{ fontSize: 12, marginLeft: "auto" }}
                title="Select all Not Started orders on this page"
                data-testid="select-eligible-page-orders-btn"
              >
                {eligiblePageOrderIds.every((id) => selectedOrderIds.includes(id))
                  ? "Deselect Eligible"
                  : `Select Eligible (${eligiblePageOrderIds.length})`}
              </button>
            )}
          </div>

          {/* Bulk Selection Action Toolbar */}
          {selectedOrderIds.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#eff6ff",
                border: "1.5px solid #bfdbfe",
                borderRadius: 8,
                padding: "8px 14px",
                marginBottom: 14,
                boxShadow: "0 2px 5px rgba(37,99,235,0.08)",
              }}
              data-testid="production-bulk-selection-bar"
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#1e3a8a", fontWeight: 600 }}>
                <CheckCircle2 size={16} color="#2563eb" />
                <span>{selectedOrderIds.length} orders selected</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  className="outline-btn compact"
                  onClick={() => setSelectedOrderIds([])}
                  style={{ fontSize: 12 }}
                >
                  Deselect All
                </button>
                {canAct && (
                  <button
                    type="button"
                    className="primary-btn compact"
                    style={{ background: "#16a34a", borderColor: "#16a34a", display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600 }}
                    onClick={() => {
                      const selectedObjects = rawProductionOrders.filter((o) => selectedOrderIds.includes(o.order_id));
                      setBulkStartModalData(selectedObjects);
                    }}
                    data-testid="bulk-start-selected-btn"
                  >
                    <Play size={12} fill="currentColor" /> Start Production for Selected ({selectedOrderIds.length})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Production Queue Items Container */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {paginatedProductionItems.map((item) => {
              if (item.isBatch) {
                return (
                  <BulkProductionBatchCard
                    key={item.batch_id}
                    batch={item}
                    canAct={canAct}
                    warehouseMap={warehouseMap}
                    user={user}
                    onOpenOrder={onOpen}
                    onOpenBatch={(batchId) => onOpen(batchId)}
                    onOpenAssignModal={(b) => setBatchAssignModalData(b)}
                    onOpenAssignUserModal={(b) => setAssignUserModalData({ mode: "batch", batch: b, targetOrders: b.orders })}
                    onAssignUserSingle={(o) => setAssignUserModalData({ mode: "single", order: o, targetOrders: [o] })}
                    onOpenStartModal={(b) => setBatchStartModalData(b)}
                    onOpenBatchHoldModal={(b) => setBatchHoldModalData(b)}
                    onOpenBatchResumeModal={(b) => setBatchResumeModalData(b)}
                    onOpenBatchCompleteModal={(b) => setBatchCompleteModalData(b)}
                    onQuickStartOrder={handleQuickStartOrder}
                    onOpenHoldModal={handleOpenHoldModal}
                    onOpenCompleteModal={handleOpenCompleteModal}
                    onQuickResumeOrder={handleQuickResumeOrder}
                    onReadyForDispatch={handleReadyForDispatch}
                    onBatchReadyForDispatch={handleBatchReadyForDispatch}
                    busyOrderIds={busyOrderIds}
                    optimisticStatus={optimisticStatus}
                  />
                );
              }

              // Standalone Single Order Card
              const prodSt = optimisticStatus[item.order_id]?.production_status || item.production_status || "Not Started";
              const isUrgent = item.priority === "Urgent";
              const isHigh = item.priority === "High";
              const isBusy = busyOrderIds.has(item.order_id);
              const isSelected = selectedOrderIds.includes(item.order_id);
              const hasVendor = Boolean(item.production_vendor_name || item.assigned_producer);
              const hasWarehouse = Boolean(item.warehouse_name);
              const isEligibleToStart = prodSt === "Not Started";
              const canDispatch = canAct && prodSt === "Completed";

              return (
                <div
                  key={item.order_id}
                  className="panel"
                  style={{
                    margin: 0,
                    padding: "12px 16px",
                    cursor: "pointer",
                    border: isSelected ? "1.5px solid #3b82f6" : "1px solid #e2e8f0",
                    background: isSelected ? "#f8faff" : "#ffffff",
                    borderRadius: 8,
                    transition: "all 0.15s ease",
                  }}
                  onClick={() => onOpen(item.order_id)}
                  data-testid={`single-production-card-${item.order_id}`}
                >
                  {/* Top Row: Checkbox, Order ID, Priority, Due Date & Status Badge */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {canAct && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedOrderIds((prev) =>
                              prev.includes(item.order_id)
                                ? prev.filter((id) => id !== item.order_id)
                                : [...prev, item.order_id]
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: 15, height: 15, cursor: "pointer" }}
                          title="Select order"
                          data-testid={`select-order-${item.order_id}`}
                        />
                      )}
                      <b className="mono" style={{ color: "#2563eb", fontSize: 13 }}>#{item.order_id}</b>
                      <span className={`badge ${isUrgent ? "red" : isHigh ? "amber" : "blue"}`} style={{ fontSize: 10 }}>
                        {item.priority || "Normal"}
                      </span>
                      {item.required_delivery_date ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#64748b" }}>
                          <Calendar size={12} /> Due {item.required_delivery_date}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>No due date</span>
                      )}
                    </div>

                    <span
                      className={`badge ${
                        prodSt === "Completed"
                          ? "green"
                          : prodSt === "In Progress"
                          ? "purple"
                          : prodSt === "On Hold"
                          ? "amber"
                          : "blue"
                      }`}
                      style={{ fontSize: 11, fontWeight: 700, cursor: "default", userSelect: "none" }}
                      data-testid={`single-order-status-badge-${item.order_id}`}
                    >
                      {prodSt}
                    </span>
                  </div>

                  {/* Body Grid: Client & Product | Quantity & Value | Vendor | Assigned User | Warehouse | Timeline | Smart Actions */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.3fr 0.8fr 1fr 1fr 0.8fr 0.9fr auto",
                      gap: 12,
                      alignItems: "center",
                    }}
                  >
                    {/* 1. Client & Product */}
                    <div style={{ minWidth: 0 }}>
                      <h4 style={{ margin: 0, fontSize: 13.5, color: "var(--navy)", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.client_name}>
                        {item.client_name}
                      </h4>
                      <div style={{ fontSize: 13, color: "#334155", fontWeight: 600, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.product}>
                        {item.product}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: item.description?.trim() ? "#475569" : "#94a3b8",
                          fontStyle: item.description?.trim() ? "normal" : "italic",
                          marginTop: 2,
                          lineHeight: 1.35,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.description?.trim() || "No description provided"}
                      >
                        {item.description?.trim() ? item.description.trim() : "No description provided"}
                      </div>
                      {item.design_specs && (
                        <div
                          style={{ fontSize: 10.5, color: "#64748b", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={`Specs: ${item.design_specs}`}
                        >
                          Specs: {item.design_specs}
                        </div>
                      )}
                      {item.client_contact_person && (
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>Contact: {item.client_contact_person}</div>
                      )}
                    </div>

                    {/* 2. Quantity & Value */}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>
                        {(item.quantity || 0).toLocaleString()} {item.unit || "units"}
                      </div>
                      {item.total_value > 0 ? (
                        <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 600, marginTop: 2 }}>{compactMoney(item.total_value)}</div>
                      ) : (
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>—</div>
                      )}
                    </div>

                    {/* 3. Production Vendor */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#64748b", marginBottom: 2 }}>Production Vendor</div>
                      {item.production_vendor_name ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#1e293b", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.production_vendor_name}>
                          <Factory size={13} color="#4f46e5" style={{ flexShrink: 0 }} />
                          <span>{item.production_vendor_name}</span>
                        </div>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            color: "#d97706",
                            background: "#fffbeb",
                            border: "1px solid #fde68a",
                            padding: "1px 5px",
                            borderRadius: 3,
                            fontSize: 10.5,
                            fontWeight: 600,
                          }}
                        >
                          <AlertCircle size={10} /> Unassigned
                        </span>
                      )}
                    </div>

                    {/* 4. Assigned Production User */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, marginBottom: 2 }}>
                        <span style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#64748b" }}>Assigned User</span>
                        {canAct && (isAdmin(user) || (user?.role || "").toLowerCase() === "admin") && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignUserModalData({ mode: "single", order: item, targetOrders: [item] });
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              fontSize: 10,
                              color: "#2563eb",
                              fontWeight: 600,
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            title="Assign or change production user"
                            data-testid={`assign-user-btn-${item.order_id}`}
                          >
                            {item.assigned_producer || item.assigned_production_user ? "Change" : "Assign"}
                          </button>
                        )}
                      </div>
                      {item.assigned_producer || item.assigned_production_user ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#1e293b", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.assigned_producer || item.assigned_production_user}>
                          <User size={13} color="#059669" style={{ flexShrink: 0 }} />
                          <span>{item.assigned_producer || item.assigned_production_user}</span>
                        </div>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            color: "#b45309",
                            background: "#fef3c7",
                            border: "1px solid #fde68a",
                            padding: "1px 5px",
                            borderRadius: 3,
                            fontSize: 10.5,
                            fontWeight: 600,
                          }}
                        >
                          <AlertCircle size={10} /> Unassigned
                        </span>
                      )}
                    </div>

                    {/* 5. Warehouse */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#64748b", marginBottom: 2 }}>Warehouse</div>
                      {item.warehouse_name ? (
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#1e293b", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.warehouse_name}>
                            <Warehouse size={13} color="#0284c7" style={{ flexShrink: 0 }} />
                            <span>{item.warehouse_name}</span>
                          </div>
                          {(() => {
                            const whUsers = getWarehouseUsersForOrder(item);
                            if (!whUsers || whUsers.length === 0) return null;
                            return (
                              <div
                                style={{
                                  fontSize: 10.5,
                                  color: "#0369a1",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  marginTop: 2,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={`Warehouse Production Staff: ${whUsers.join(", ")}`}
                              >
                                <User size={10} color="#0284c7" />
                                <span>WH Staff: {whUsers.join(", ")}</span>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>
                      )}
                    </div>

                    {/* 6. Timeline / Expected Date */}
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#334155" }}>
                        <Clock size={12} color="#64748b" />
                        {prodSt === "On Hold" && (item.hold_reason || item.production_remarks) ? (
                          <span style={{ fontWeight: 600, color: "#b45309" }} title={item.hold_reason || item.production_remarks}>
                            Hold: {item.hold_reason || item.production_remarks}
                          </span>
                        ) : item.estimated_ready_date || item.production_expected ? (
                          <span style={{ fontWeight: 600 }}>Ready: {item.estimated_ready_date || item.production_expected}</span>
                        ) : item.production_start_date ? (
                          <span>Started: {item.production_start_date}</span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>No date set</span>
                        )}
                      </div>
                    </div>

                    {/* 5. Smart Status Action Buttons */}
                    <div style={{ textAlign: "right", display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" }}>
                      {/* NOT STARTED: Start Production + Manage */}
                      {canAct && prodSt === "Not Started" && (
                        <button
                          type="button"
                          className="prod-action-btn prod-action-btn-start btn-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickStartOrder(item);
                          }}
                          disabled={isBusy}
                          title={!hasVendor && !hasWarehouse ? "Vendor/Warehouse required to start production" : "Start production now"}
                          data-testid={`quick-start-btn-${item.order_id}`}
                        >
                          <Play size={11} fill="currentColor" />
                          <span>{isBusy ? "Starting..." : "Start Production"}</span>
                        </button>
                      )}

                      {/* IN PROGRESS: Hold + Complete + Manage */}
                      {canAct && prodSt === "In Progress" && (
                        <>
                          <button
                            type="button"
                            className="prod-action-btn prod-action-btn-hold btn-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenHoldModal(item);
                            }}
                            disabled={isBusy}
                            title="Hold Production"
                            data-testid={`quick-hold-btn-${item.order_id}`}
                          >
                            <Pause size={11} />
                            <span>Hold</span>
                          </button>

                          <button
                            type="button"
                            className="prod-action-btn prod-action-btn-complete btn-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenCompleteModal(item);
                            }}
                            disabled={isBusy}
                            title="Complete Production"
                            data-testid={`quick-complete-btn-${item.order_id}`}
                          >
                            <CheckCircle2 size={12} />
                            <span>Complete</span>
                          </button>
                        </>
                      )}

                      {/* ON HOLD: Resume Production + Complete + Manage */}
                      {canAct && prodSt === "On Hold" && (
                        <>
                          <button
                            type="button"
                            className="prod-action-btn prod-action-btn-resume btn-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuickResumeOrder(item);
                            }}
                            disabled={isBusy}
                            title="Resume production back to In Progress"
                            data-testid={`quick-resume-btn-${item.order_id}`}
                          >
                            <Play size={11} fill="currentColor" />
                            <span>{isBusy ? "Resuming..." : "Resume Production"}</span>
                          </button>

                          <button
                            type="button"
                            className="prod-action-btn prod-action-btn-complete btn-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenCompleteModal(item);
                            }}
                            disabled={isBusy}
                            title="Complete Production"
                            data-testid={`quick-complete-btn-${item.order_id}`}
                          >
                            <CheckCircle2 size={12} />
                            <span>Complete</span>
                          </button>
                        </>
                      )}

                      {/* COMPLETED: Ready for Dispatch action */}
                      {canDispatch && (
                        <button
                          type="button"
                          className="prod-action-btn prod-action-btn-dispatch btn-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReadyForDispatch(item.order_id);
                          }}
                          disabled={isBusy}
                          title="Move this completed order to Ready for Dispatch"
                          data-testid={`quick-dispatch-btn-${item.order_id}`}
                        >
                          <Send size={11} />
                          <span>{isBusy ? "Sending..." : "Ready for Dispatch"}</span>
                        </button>
                      )}

                      {/* ALL STATUSES: Manage button */}
                      <button
                        type="button"
                        className="prod-action-btn prod-action-btn-manage btn-md"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(item.order_id);
                        }}
                        data-testid={`manage-production-btn-${item.order_id}`}
                      >
                        Manage
                      </button>

                    </div>
                  </div>

                  {/* Optional On-Hold remarks banner */}
                  {prodSt === "On Hold" && (item.hold_reason || item.production_remarks) && (
                    <div
                      style={{
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        borderRadius: 6,
                        padding: "6px 10px",
                        marginTop: 8,
                        fontSize: 12,
                        color: "#92400e",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <AlertCircle size={13} style={{ flexShrink: 0 }} />
                      <span>
                        <strong>Hold Reason:</strong> {item.hold_reason || item.production_remarks}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}

            {filteredProductionItems.length === 0 && (
              <div className="panel" style={{ padding: "48px 24px", textAlign: "center", border: "1px dashed #cbd5e1" }} data-testid="production-empty-state">
                <div style={{ color: "var(--navy)", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>No orders in production</div>
                <span className="muted" style={{ fontSize: 13 }}>
                  {orderSearch || prodStatusFilter || prodPriorityFilter
                    ? "No production orders match your active search or filter criteria."
                    : "There are currently no orders in the production stage."}
                </span>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredProductionItems.length > 0 && (
            <div style={{ padding: "14px 0 0", borderTop: "1px solid #f1f5f9", marginTop: 14 }}>
              <Pagination
                page={ordersPage}
                pageSize={ordersPageSize}
                totalItems={filteredProductionItems.length}
                onPageChange={setOrdersPage}
                onPageSizeChange={(newSize) => {
                  setOrdersPageSize(newSize);
                  setOrdersPage(1);
                }}
                itemName="production items"
              />
            </div>
          )}
        </div>
      )}

      {/* 4. TAB CONTENT: VENDOR REQUESTS */}
      {tab === "requests" && (
        <div data-testid="vendor-requests-view">
          {/* Summary Metrics */}
          <div className="metrics-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
            <MetricBox
              label="Total Requests"
              value={requestMetrics.total}
              tone="blue"
              active={!statusFilter}
              onClick={() => {
                setStatusFilter("");
                setPage(1);
              }}
            />
            <MetricBox
              label="Pending Details"
              value={requestMetrics.pending}
              tone="amber"
              active={statusFilter === "pending"}
              onClick={() => {
                setStatusFilter(statusFilter === "pending" ? "" : "pending");
                setPage(1);
              }}
            />
            <MetricBox
              label="Confirmed Details"
              value={requestMetrics.confirmed}
              tone="green"
              active={statusFilter === "confirmed"}
              onClick={() => {
                setStatusFilter(statusFilter === "confirmed" ? "" : "confirmed");
                setPage(1);
              }}
            />
          </div>

          {/* Search & Filters Toolbar */}
          <div className="toolbar" style={{ marginTop: 14, marginBottom: 16 }}>
            <div className="search-box">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search Request ID, batch ID, client, product…"
                value={requestSearch}
                onChange={(e) => {
                  setRequestSearch(e.target.value);
                  setPage(1);
                }}
                data-testid="vendor-requests-search"
              />
              {requestSearch && (
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => {
                    setRequestSearch("");
                    setPage(1);
                  }}
                  data-testid="vendor-requests-search-clear"
                  style={{ padding: 2, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              data-testid="vendor-requests-status-filter"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending Details</option>
              <option value="confirmed">Confirmed Details</option>
            </select>

            <select
              value={reqPriorityFilter}
              onChange={(e) => {
                setReqPriorityFilter(e.target.value);
                setPage(1);
              }}
              data-testid="vendor-requests-priority-filter"
            >
              <option value="">All Priorities</option>
              <option value="Urgent">Urgent</option>
              <option value="High">High</option>
              <option value="Normal">Normal</option>
            </select>

            {(requestSearch || statusFilter || reqPriorityFilter) && (
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => {
                  setRequestSearch("");
                  setStatusFilter("");
                  setReqPriorityFilter("");
                  setPage(1);
                }}
                style={{ fontSize: 12 }}
                title="Reset all filters"
              >
                Reset Filters
              </button>
            )}
          </div>

          {/* Requests List */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {paginatedRequests.map((item) => {
              if (item.isBatch) {
                return (
                  <GroupedVendorRequestCard
                    key={item.batch_id}
                    batch={item}
                    canAct={canAct}
                    onSelectOrder={(r) => setSelectedRequest(r)}
                    onBatchAssign={(b) => setSelectedBatchRequest(b)}
                  />
                );
              }

              // Standalone Single Request
              const isPending = item.status === "pending" || item.status === "requested";
              const isConfirmed = item.status === "confirmed";

              return (
                <div
                  key={item.request_id || item.order_id}
                  className="panel"
                  style={{
                    margin: 0,
                    padding: "14px 16px",
                    cursor: "pointer",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                  }}
                  onClick={() => setSelectedRequest(item)}
                  data-testid={`request-card-${item.order_id}`}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <b className="mono" style={{ color: "#2563eb" }}>#{item.order_id}</b>
                      <span className={`badge ${item.priority === "Urgent" ? "red" : item.priority === "High" ? "amber" : "blue"}`} style={{ fontSize: 10 }}>
                        {item.priority || "Normal"}
                      </span>
                      {item.required_delivery_date && (
                        <small className="muted">Due {item.required_delivery_date}</small>
                      )}
                    </div>
                    <span className={`badge ${isConfirmed ? "green" : "amber"}`} style={{ fontSize: 11 }}>
                      {isConfirmed ? "Confirmed" : "Pending Details"}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr auto", gap: 12, alignItems: "center" }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 14, color: "var(--navy)" }}>{item.client_name}</h4>
                      <div style={{ fontSize: 13, color: "#334155", fontWeight: 600, marginTop: 2 }}>{item.product}</div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: item.description?.trim() ? "#475569" : "#94a3b8",
                          fontStyle: item.description?.trim() ? "normal" : "italic",
                          marginTop: 2,
                          lineHeight: 1.35,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.description?.trim() || "No description provided"}
                      >
                        {item.description?.trim() ? item.description.trim() : "No description provided"}
                      </div>
                      {item.client_contact_person && (
                        <small className="muted">Contact: {item.client_contact_person}</small>
                      )}
                    </div>

                    <div>
                      <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Quantity</small>
                      <b>{(item.quantity || 0).toLocaleString()} units</b>
                    </div>

                    <div>
                      <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Vendor</small>
                      {item.vendor_name ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                          <Factory size={13} color="#4f46e5" />
                          <b>{item.vendor_name}</b>
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
                      )}
                    </div>

                    <div>
                      <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Warehouse</small>
                      {item.warehouse_name ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                          <Warehouse size={13} color="#059669" />
                          <b>{item.warehouse_name}</b>
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
                      )}
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <button
                        type="button"
                        className={isPending ? "primary-btn compact" : "outline-btn compact"}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRequest(item);
                        }}
                        data-testid={`action-assign-details-${item.order_id}`}
                      >
                        {isPending ? "Assign Details" : "Update Details"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredRequests.length === 0 && (
              <div className="panel" style={{ padding: "48px 24px", textAlign: "center", border: "1px dashed #cbd5e1" }} data-testid="vendor-requests-empty">
                <div style={{ color: "var(--navy)", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                  {loadingRequests ? "Loading vendor requests..." : "No vendor requests"}
                </div>
                <span className="muted" style={{ fontSize: 13 }}>
                  {requestSearch || statusFilter
                    ? "No vendor requests match your active search or filter criteria."
                    : "No pending vendor or warehouse assignment requests from the Design team."}
                </span>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredRequests.length > 0 && (
            <div style={{ padding: "14px 0 0", borderTop: "1px solid #f1f5f9", marginTop: 14 }}>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalItems={filteredRequests.length}
                onPageChange={setPage}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                }}
                itemName="requests"
              />
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODALS: SHARED PRODUCTION MODALS                                          */}
      {/* ========================================================================= */}
      {/* Single Production Request Detail Modal */}
      {selectedRequest && (
        <ProductionRequestModal
          request={selectedRequest}
          vendors={vendors}
          warehouses={warehouses}
          canAct={canAct}
          onClose={() => setSelectedRequest(null)}
          onSuccess={() => {
            setSelectedRequest(null);
            loadRequests();
            onChange && onChange();
          }}
        />
      )}

      {/* Batch Vendor Request Modal */}
      {selectedBatchRequest && (
        <BatchVendorRequestModal
          batch={selectedBatchRequest}
          vendors={vendors}
          warehouses={warehouses}
          canAct={canAct}
          onClose={() => setSelectedBatchRequest(null)}
          onSuccess={() => {
            setSelectedBatchRequest(null);
            loadRequests();
            onChange && onChange();
          }}
        />
      )}

      {/* Batch Assign Vendor/Warehouse Modal (Orders Tab) */}
      {batchAssignModalData && (
        <BatchAssignModal
          batch={batchAssignModalData}
          vendors={vendors}
          warehouses={warehouses}
          onClose={() => setBatchAssignModalData(null)}
          onSave={async ({ vendorId, vendorName, warehouseId, warehouseName, orderIds }) => {
            const v = vendors.find((x) => x.vendor_id === vendorId);
            const w = warehouses.find((x) => x.warehouse_id === warehouseId);
            try {
              await api("patch", `/orders/batch/${batchAssignModalData.batch_id}/production`, {
                order_ids: orderIds,
                vendor_id: vendorId || "",
                vendor_name: vendorName || v?.name || v?.vendor_name || "",
                warehouse_id: warehouseId || "",
                warehouse_name: warehouseName || w?.name || w?.warehouse_name || "",
              });
              toast.success("Vendor/Warehouse assigned to batch");
              setBatchAssignModalData(null);
              onChange && onChange();
            } catch (err) {
              toast.error("Could not update batch assignment");
            }
          }}
        />
      )}

      {/* Quick Start Single Order Modal (when vendor/warehouse missing) */}
      {quickStartModalOrder && (
        <QuickStartProductionModal
          order={quickStartModalOrder}
          vendors={vendors}
          warehouses={warehouses}
          onClose={() => setQuickStartModalOrder(null)}
          onStarted={(updatedOrder) => {
            setOptimisticStatus((prev) => ({
              ...prev,
              [updatedOrder.order_id]: {
                production_status: "In Progress",
                production_vendor_name: updatedOrder.production_vendor_name,
                production_vendor_id: updatedOrder.production_vendor_id,
                warehouse_name: updatedOrder.warehouse_name,
                warehouse_id: updatedOrder.warehouse_id,
                production_start_date: updatedOrder.production_start_date,
              },
            }));
            onChange && onChange();
          }}
        />
      )}

      {/* Batch Start Production Modal (for BulkProductionBatchCard) */}
      {batchStartModalData && (
        <BatchStartModal
          batch={batchStartModalData}
          vendors={vendors}
          warehouses={warehouses}
          onClose={() => setBatchStartModalData(null)}
          onStarted={(startedIds, sDate) => {
            const todayStr = sDate || getTodayDateString();
            setOptimisticStatus((prev) => {
              const next = { ...prev };
              (startedIds || []).forEach((id) => {
                next[id] = {
                  ...next[id],
                  production_status: "In Progress",
                  production_start_date: todayStr,
                  current_stage: "Production",
                };
              });
              return next;
            });
            onChange && onChange();
          }}
        />
      )}

      {/* Batch Hold Production Modal */}
      {batchHoldModalData && (
        <BatchHoldModal
          batch={batchHoldModalData}
          optimisticStatus={optimisticStatus}
          onClose={() => setBatchHoldModalData(null)}
          onConfirm={handleBatchHold}
        />
      )}

      {/* Batch Resume Production Modal */}
      {batchResumeModalData && (
        <BatchResumeModal
          batch={batchResumeModalData}
          optimisticStatus={optimisticStatus}
          onClose={() => setBatchResumeModalData(null)}
          onConfirm={handleBatchResume}
        />
      )}

      {/* Batch Complete Production Modal */}
      {batchCompleteModalData && (
        <BatchCompleteModal
          batch={batchCompleteModalData}
          optimisticStatus={optimisticStatus}
          onClose={() => setBatchCompleteModalData(null)}
          onConfirm={handleBatchComplete}
        />
      )}

      {/* Bulk Start Production Modal (for multi-selected standalone orders) */}
      {bulkStartModalData && (
        <BulkStartOrdersModal
          selectedOrders={bulkStartModalData}
          vendors={vendors}
          warehouses={warehouses}
          onClose={() => setBulkStartModalData(null)}
          onStarted={(startedIds) => {
            const nextOverrides = {};
            startedIds.forEach((id) => {
              nextOverrides[id] = { production_status: "In Progress", production_start_date: getTodayDateString() };
            });
            setOptimisticStatus((prev) => ({ ...prev, ...nextOverrides }));
            setSelectedOrderIds([]);
            onChange && onChange();
          }}
        />
      )}

      {/* Put on Hold Modal */}
      {holdModalOrder && (
        <HoldProductionModal
          order={holdModalOrder}
          onClose={() => setHoldModalOrder(null)}
          onConfirm={handleConfirmPutOnHold}
        />
      )}

      {/* Complete Production Modal */}
      {completeModalOrder && (
        <CompleteProductionModal
          order={completeModalOrder}
          onClose={() => setCompleteModalOrder(null)}
          onConfirm={handleConfirmComplete}
        />
      )}

      {/* Assign Production User Modal */}
      {assignUserModalData && (
        <AssignProductionUserModal
          data={assignUserModalData}
          users={productionUsers}
          onClose={() => setAssignUserModalData(null)}
          onSave={handleSaveProductionUser}
        />
      )}
    </div>
  );
}



// -------------------------------------------------------------
// Bulk Production Request / Batch Queue Card
// -------------------------------------------------------------
function BulkProductionBatchCard({
  batch,
  canAct,
  warehouseMap,
  user,
  onOpenOrder,
  onOpenBatch,
  onOpenAssignModal,
  onOpenAssignUserModal,
  onAssignUserSingle,
  onOpenStartModal,
  onOpenBatchHoldModal,
  onOpenBatchResumeModal,
  onOpenBatchCompleteModal,
  onQuickStartOrder,
  onOpenHoldModal,
  onOpenCompleteModal,
  onQuickResumeOrder,
  onReadyForDispatch,
  onBatchReadyForDispatch,
  busyOrderIds,
  optimisticStatus,
}) {
  const isUrgent = batch.priority === "Urgent";
  const isHigh = batch.priority === "High";
  const canSeePhone = canViewClientPhone(user);

  // Derive overall batch production status and counts with optimistic synchronization
  const ordersList = batch.orders || [];
  const totalCount = ordersList.length;
  let notStartedCount = 0;
  let inProgressCount = 0;
  let onHoldCount = 0;
  let completedCount = 0;

  ordersList.forEach((o) => {
    const st = optimisticStatus?.[o.order_id]?.production_status || o.production_status || "Not Started";
    if (st === "Completed") completedCount += 1;
    else if (st === "On Hold") onHoldCount += 1;
    else if (st === "In Progress") inProgressCount += 1;
    else notStartedCount += 1;
  });

  const pendingCount = totalCount - completedCount;
  const isAllCompleted = completedCount === totalCount && totalCount > 0;
  const canAssignUser = isAdmin(user);
  const uniqueVendors = Array.from(new Set(ordersList.map((o) => o.production_vendor_name).filter(Boolean)));
  const uniqueUsers = Array.from(new Set(
    ordersList.flatMap((o) => {
      const u = o.assigned_producer || o.assigned_production_user || "";
      return u.split(",").map((s) => s.trim()).filter(Boolean);
    })
  ));
  const uniqueWarehouses = Array.from(new Set(ordersList.map((o) => o.warehouse_name).filter(Boolean)));
  const batchWarehouseUsers = Array.from(new Set(
    ordersList.flatMap((o) => {
      if (o.warehouse_assigned_user_names && o.warehouse_assigned_user_names.length > 0) {
        return o.warehouse_assigned_user_names;
      }
      const wh = warehouseMap?.[o.warehouse_id] || warehouseMap?.[o.warehouse_name?.toLowerCase().trim()];
      return wh?.assigned_user_names || [];
    }).filter(Boolean)
  ));

  let batchStatus = "Not Started";
  if (isAllCompleted) {
    batchStatus = "Completed";
  } else if (inProgressCount > 0) {
    batchStatus = "In Progress";
  } else if (onHoldCount > 0) {
    batchStatus = "On Hold";
  }

  const statusBadgeStyle = (st) => {
    switch (st) {
      case "Completed":
        return { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" };
      case "In Progress":
        return { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" };
      case "On Hold":
        return { color: "#d97706", bg: "#fffbeb", border: "#fde68a" };
      default:
        return { color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd" };
    }
  };

  return (
    <div
      className="bulk-batch-card panel"
      style={{
        margin: 0,
        background: "#ffffff",
        border: "1.5px solid #cbd5e1",
        borderRadius: 9,
        padding: "16px 18px",
        boxShadow: "0 2px 6px rgba(15, 23, 42, 0.05)",
      }}
      data-testid={`bulk-production-batch-card-${batch.batch_id}`}
    >
      {/* Top Row: Batch ID, Tag, Priority & Group Status Badge */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            className="mono"
            style={{
              background: "#eff6ff",
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              padding: "2px 8px",
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            #{batch.batch_id}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              background: "#f1f5f9",
              padding: "2px 7px",
              borderRadius: 4,
              border: "1px solid #e2e8f0",
            }}
          >
            Bulk Production Request · {totalCount} Orders
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className={`badge ${isUrgent ? "red" : isHigh ? "amber" : "blue"}`} style={{ fontSize: 10 }}>
            {batch.priority}
          </span>
          <span className="badge green" style={{ fontSize: 10 }}>
            {compactMoney(batch.total_value)}
          </span>
          <span
            className={`badge ${
              batchStatus === "Completed"
                ? "green"
                : batchStatus === "In Progress"
                ? "purple"
                : batchStatus === "On Hold"
                ? "amber"
                : "blue"
            }`}
            style={{ fontSize: 11, fontWeight: 700, cursor: "default", userSelect: "none" }}
            data-testid={`bulk-batch-status-badge-${batch.batch_id}`}
          >
            {batchStatus}
          </span>
        </div>
      </div>


      {/* Client Name & Contact */}
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: "2px 0 2px", fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
          {batch.client_name}
        </h3>
        {batch.client_contact_person && batch.client_contact_person !== "—" && (
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Contact: {batch.client_contact_person}
            {canSeePhone && batch.client_phone && batch.client_phone !== "—" && ` · ${batch.client_phone}`}
          </div>
        )}
      </div>

      {/* Assignment Summary Strip */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          fontSize: 11.5,
          marginBottom: 10,
          padding: "6px 12px",
          background: "#f8fafc",
          borderRadius: 6,
          border: "1px solid #e2e8f0",
        }}
        data-testid={`bulk-batch-assignment-summary-${batch.batch_id}`}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Factory size={12} color="#4f46e5" />
          <span style={{ color: "#64748b", fontWeight: 600 }}>Production Vendor:</span>
          {uniqueVendors.length === 1 ? (
            <span style={{ fontWeight: 700, color: "var(--navy)" }}>{uniqueVendors[0]}</span>
          ) : uniqueVendors.length > 1 ? (
            <span style={{ color: "#4f46e5", fontWeight: 600 }}>Multiple Vendors ({uniqueVendors.length})</span>
          ) : (
            <span style={{ color: "#d97706", fontWeight: 600 }}>Unassigned</span>
          )}
        </div>
        <span style={{ color: "#cbd5e1" }}>•</span>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <User size={12} color="#059669" />
          <span style={{ color: "#64748b", fontWeight: 600 }}>Assigned User:</span>
          {uniqueUsers.length > 0 ? (
            <span style={{ fontWeight: 700, color: "var(--navy)" }}>{uniqueUsers.join(", ")}</span>
          ) : (
            <span style={{ color: "#d97706", fontWeight: 600 }}>Unassigned</span>
          )}
        </div>
        <span style={{ color: "#cbd5e1" }}>•</span>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Warehouse size={12} color="#0284c7" />
          <span style={{ color: "#64748b", fontWeight: 600 }}>Warehouse:</span>
          {uniqueWarehouses.length === 1 ? (
            <span style={{ fontWeight: 700, color: "var(--navy)", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span>{uniqueWarehouses[0]}</span>
              {batchWarehouseUsers.length > 0 && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#0369a1",
                    background: "#e0f2fe",
                    border: "1px solid #bae6fd",
                    padding: "0 6px",
                    borderRadius: 4,
                  }}
                  title={`Assigned Production Users for ${uniqueWarehouses[0]}: ${batchWarehouseUsers.join(", ")}`}
                >
                  Staff: {batchWarehouseUsers.join(", ")}
                </span>
              )}
            </span>
          ) : uniqueWarehouses.length > 1 ? (
            <span style={{ color: "#0284c7", fontWeight: 600 }}>Multiple Warehouses ({uniqueWarehouses.length})</span>
          ) : (
            <span style={{ color: "#94a3b8" }}>—</span>
          )}
        </div>
      </div>

      {/* Individual Products Breakdown Table */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 6,
          padding: "8px 10px",
          marginBottom: 12,
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.3fr 65px 1fr 1fr 0.9fr 95px 1fr auto",
            gap: 8,
            fontSize: 10.5,
            fontWeight: 700,
            color: "var(--muted)",
            textTransform: "uppercase",
            borderBottom: "1px solid #e2e8f0",
            paddingBottom: 5,
            marginBottom: 6,
          }}
        >
          <span>Product / Order ID</span>
          <span style={{ textAlign: "right" }}>Qty</span>
          <span>Production Vendor</span>
          <span>Assigned User</span>
          <span>Warehouse</span>
          <span>Status</span>
          <span>Timeline / Remarks</span>
          <span style={{ textAlign: "right" }}>Action</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {batch.orders.map((o) => {
            const pStatus = optimisticStatus?.[o.order_id]?.production_status || o.production_status || "Not Started";
            const bTheme = statusBadgeStyle(pStatus);
            const isBusy = busyOrderIds?.has(o.order_id);
            const canDispatch = canAct && pStatus === "Completed";

            return (
              <div
                key={o.order_id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.3fr 65px 1fr 1fr 0.9fr 95px 1fr auto",
                  gap: 8,
                  alignItems: "center",
                  fontSize: 12,
                  padding: "5px 0",
                  borderBottom: "1px solid #f1f5f9",
                }}
                data-testid={`batch-item-row-${o.order_id}`}
              >
                {/* 1. Product & Order ID */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.product}>
                    <b style={{ color: "var(--navy)" }}>{o.product}</b>
                    <span className="mono" style={{ fontSize: 10.5, color: "#64748b", marginLeft: 6 }}>
                      (#{o.order_id})
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: o.description?.trim() ? "#475569" : "#94a3b8",
                      fontStyle: o.description?.trim() ? "normal" : "italic",
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={o.description?.trim() || "No description provided"}
                  >
                    {o.description?.trim() ? o.description.trim() : "No description provided"}
                  </div>
                  {o.design_specs && (
                    <div
                      style={{
                        fontSize: 10,
                        color: "#64748b",
                        marginTop: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={`Specs: ${o.design_specs}`}
                    >
                      Specs: {o.design_specs}
                    </div>
                  )}
                </div>

                {/* 2. Quantity */}
                <div style={{ textAlign: "right", color: "#334155", fontWeight: 600 }}>
                  {o.quantity} {o.unit || "pcs"}
                </div>

                {/* 3. Production Vendor */}
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11.5 }}>
                  {o.production_vendor_name ? (
                    <span style={{ color: "var(--navy)", fontWeight: 600 }} title={o.production_vendor_name}>
                      {o.production_vendor_name}
                    </span>
                  ) : (
                    <span style={{ color: "#d97706", background: "#fffbeb", border: "1px solid #fde68a", padding: "1px 5px", borderRadius: 3, fontSize: 10, fontWeight: 600 }}>
                      Unassigned
                    </span>
                  )}
                </div>

                {/* 4. Assigned Production User */}
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11.5, display: "flex", alignItems: "center", gap: 4 }}>
                  {o.assigned_producer || o.assigned_production_user ? (
                    <span style={{ color: "#059669", fontWeight: 600 }} title={o.assigned_producer || o.assigned_production_user}>
                      {o.assigned_producer || o.assigned_production_user}
                    </span>
                  ) : (
                    <span style={{ color: "#b45309", background: "#fef3c7", border: "1px solid #fde68a", padding: "1px 5px", borderRadius: 3, fontSize: 10, fontWeight: 600 }}>
                      Unassigned
                    </span>
                  )}
                  {canAssignUser && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAssignUserSingle && onAssignUserSingle(o);
                      }}
                      style={{ background: "none", border: "none", padding: 0, color: "#2563eb", cursor: "pointer", fontSize: 10, textDecoration: "underline" }}
                      title="Assign or change production user"
                    >
                      {o.assigned_producer || o.assigned_production_user ? "Edit" : "Set"}
                    </button>
                  )}
                </div>

                {/* 5. Warehouse */}
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11.5 }}>
                  {o.warehouse_name ? (
                    <div>
                      <div style={{ color: "#334155", fontWeight: 500 }} title={o.warehouse_name}>{o.warehouse_name}</div>
                      {(() => {
                        const whUsers = (o.warehouse_assigned_user_names && o.warehouse_assigned_user_names.length > 0)
                          ? o.warehouse_assigned_user_names
                          : (warehouseMap?.[o.warehouse_id]?.assigned_user_names || warehouseMap?.[o.warehouse_name?.toLowerCase().trim()]?.assigned_user_names || []);
                        if (!whUsers || whUsers.length === 0) return null;
                        return (
                          <div
                            style={{
                              fontSize: 10,
                              color: "#0369a1",
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                              marginTop: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={`Warehouse Production Staff: ${whUsers.join(", ")}`}
                          >
                            <User size={9} color="#0284c7" />
                            <span>{whUsers.join(", ")}</span>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                  )}
                </div>

                {/* 6. Status Badge */}
                <div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "2px 6px",
                      borderRadius: 3,
                      color: bTheme.color,
                      background: bTheme.bg,
                      border: `1px solid ${bTheme.border}`,
                      display: "inline-block",
                      whiteSpace: "nowrap",
                      cursor: "default",
                      userSelect: "none",
                    }}
                    data-testid={`batch-item-status-badge-${o.order_id}`}
                  >
                    {pStatus}
                  </span>
                </div>

                {/* 7. Timeline / Remarks */}
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11 }}>
                  {pStatus === "On Hold" && (o.hold_reason || o.production_remarks) ? (
                    <span style={{ color: "#b45309", fontWeight: 600 }}>
                      Hold: {o.hold_reason || o.production_remarks}
                    </span>
                  ) : o.estimated_ready_date || o.production_expected ? (
                    <span style={{ color: "#475569" }}>
                      Ready: {o.estimated_ready_date || o.production_expected}
                    </span>
                  ) : o.production_start_date ? (
                    <span style={{ color: "#64748b" }}>
                      Started: {o.production_start_date}
                    </span>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ textAlign: "right", display: "flex", gap: 5, justifyContent: "flex-end", alignItems: "center" }}>
                  {/* Not Started: Start + Manage */}
                  {canAct && pStatus === "Not Started" && (
                    <button
                      type="button"
                      className="prod-action-btn prod-action-btn-start btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onQuickStartOrder && onQuickStartOrder(o);
                      }}
                      disabled={isBusy}
                      title="Start production for this item"
                      data-testid={`batch-item-start-btn-${o.order_id}`}
                    >
                      <Play size={10} fill="currentColor" /> {isBusy ? "..." : "Start"}
                    </button>
                  )}

                  {/* In Progress: Hold + Complete + Manage */}
                  {canAct && pStatus === "In Progress" && (
                    <>
                      <button
                        type="button"
                        className="prod-action-btn prod-action-btn-hold btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenHoldModal && onOpenHoldModal(o);
                        }}
                        disabled={isBusy}
                        title="Hold Production"
                        data-testid={`batch-item-hold-btn-${o.order_id}`}
                      >
                        <Pause size={10} /> Hold
                      </button>
                      <button
                        type="button"
                        className="prod-action-btn prod-action-btn-complete btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenCompleteModal && onOpenCompleteModal(o);
                        }}
                        disabled={isBusy}
                        title="Complete Production"
                        data-testid={`batch-item-complete-btn-${o.order_id}`}
                      >
                        <CheckCircle2 size={11} /> Complete
                      </button>
                    </>
                  )}

                  {/* On Hold: Resume Production + Complete + Manage */}
                  {canAct && pStatus === "On Hold" && (
                    <>
                      <button
                        type="button"
                        className="prod-action-btn prod-action-btn-resume btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onQuickResumeOrder && onQuickResumeOrder(o);
                        }}
                        disabled={isBusy}
                        title="Resume production back to In Progress"
                        data-testid={`batch-item-resume-btn-${o.order_id}`}
                      >
                        <Play size={10} fill="currentColor" /> {isBusy ? "..." : "Resume"}
                      </button>
                      <button
                        type="button"
                        className="prod-action-btn prod-action-btn-complete btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenCompleteModal && onOpenCompleteModal(o);
                        }}
                        disabled={isBusy}
                        title="Complete Production"
                        data-testid={`batch-item-complete-btn-${o.order_id}`}
                      >
                        <CheckCircle2 size={11} /> Complete
                      </button>
                    </>
                  )}

                  {/* Completed: Ready for Dispatch action */}
                  {canDispatch && (
                    <button
                      type="button"
                      className="prod-action-btn prod-action-btn-dispatch btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReadyForDispatch && onReadyForDispatch(o.order_id);
                      }}
                      disabled={isBusy}
                      title="Hand over this product to Dispatch"
                      data-testid={`batch-item-dispatch-btn-${o.order_id}`}
                    >
                      <Send size={10} /> {isBusy ? "..." : "Ready for Dispatch"}
                    </button>
                  )}

                  {/* ALL STATUSES: Manage button */}
                  <button
                    type="button"
                    className="prod-action-btn prod-action-btn-manage btn-sm"
                    onClick={() => onOpenOrder(o.order_id)}
                    data-testid={`batch-item-manage-btn-${o.order_id}`}
                  >
                    Manage
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer: Summary breakdown & Action buttons */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#64748b" }}>
          {isAllCompleted ? (
            <span
              style={{
                color: "#059669",
                background: "#ecfdf5",
                border: "1px solid #a7f3d0",
                padding: "3px 8px",
                borderRadius: 4,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
              data-testid={`bulk-batch-all-completed-${batch.batch_id}`}
            >
              <CheckCircle2 size={13} color="#059669" /> All production completed ({completedCount}/{totalCount} products)
            </span>
          ) : (
            <>
              <span style={{ fontWeight: 700, color: "var(--navy)" }}>Summary:</span>
              <span style={{ color: completedCount > 0 ? "#059669" : "#64748b", fontWeight: 600 }}>
                {completedCount} Completed
              </span>
              <span>·</span>
              <span style={{ color: pendingCount > 0 ? "#b45309" : "#64748b", fontWeight: 600 }}>
                {pendingCount} Pending
              </span>
              <span style={{ color: "#94a3b8", fontSize: 11 }}>
                ({notStartedCount} Not Started, {inProgressCount} In Progress{onHoldCount > 0 ? `, ${onHoldCount} On Hold` : ""})
              </span>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {canAct && (
            <>
              {/* Start Production: Start all eligible products that have not started */}
              {notStartedCount > 0 && (
                <button
                  type="button"
                  className="prod-action-btn prod-action-btn-start btn-md"
                  style={{ fontWeight: 600 }}
                  onClick={() => onOpenStartModal && onOpenStartModal(batch)}
                  disabled={busyOrderIds?.has(batch.batch_id)}
                  data-testid={`batch-card-start-btn-${batch.batch_id}`}
                  title={`Start production for ${notStartedCount} eligible product(s)`}
                >
                  <Play size={12} fill="currentColor" /> {busyOrderIds?.has(batch.batch_id) ? "Starting..." : `Start Production (${notStartedCount})`}
                </button>
              )}

              {/* Hold All: Put all eligible products currently In Progress on hold */}
              {inProgressCount > 0 && (
                <button
                  type="button"
                  className="prod-action-btn prod-action-btn-hold btn-md"
                  style={{ fontWeight: 600 }}
                  onClick={() => onOpenBatchHoldModal && onOpenBatchHoldModal(batch)}
                  disabled={busyOrderIds?.has(batch.batch_id)}
                  data-testid={`batch-card-hold-all-btn-${batch.batch_id}`}
                  title={`Put ${inProgressCount} in-progress product(s) on hold`}
                >
                  <Pause size={12} /> {busyOrderIds?.has(batch.batch_id) ? "Holding..." : `Hold All (${inProgressCount})`}
                </button>
              )}

              {/* Resume Production: Resume products that are currently On Hold */}
              {onHoldCount > 0 && (
                <button
                  type="button"
                  className="prod-action-btn prod-action-btn-resume btn-md"
                  style={{ fontWeight: 600 }}
                  onClick={() => onOpenBatchResumeModal && onOpenBatchResumeModal(batch)}
                  disabled={busyOrderIds?.has(batch.batch_id)}
                  data-testid={`batch-card-resume-btn-${batch.batch_id}`}
                  title={`Resume production for ${onHoldCount} on-hold product(s)`}
                >
                  <Play size={12} fill="currentColor" /> {busyOrderIds?.has(batch.batch_id) ? "Resuming..." : `Resume Production (${onHoldCount})`}
                </button>
              )}

              {/* Complete All: Mark all eligible products currently In Progress as Completed */}
              {inProgressCount > 0 && (
                <button
                  type="button"
                  className="prod-action-btn prod-action-btn-complete btn-md"
                  style={{ fontWeight: 600 }}
                  onClick={() => onOpenBatchCompleteModal && onOpenBatchCompleteModal(batch)}
                  disabled={busyOrderIds?.has(batch.batch_id)}
                  data-testid={`batch-card-complete-all-btn-${batch.batch_id}`}
                  title={`Mark ${inProgressCount} in-progress product(s) as Completed`}
                >
                  <CheckCircle2 size={12} /> {busyOrderIds?.has(batch.batch_id) ? "Completing..." : `Complete All (${inProgressCount})`}
                </button>
              )}

              {/* Assign Vendor: When not all products are completed */}
              {!isAllCompleted && (
                <button
                  type="button"
                  className="outline-btn compact"
                  onClick={() => onOpenAssignModal && onOpenAssignModal(batch)}
                  disabled={busyOrderIds?.has(batch.batch_id)}
                  data-testid={`batch-card-assign-btn-${batch.batch_id}`}
                >
                  <Factory size={13} /> Assign Vendor
                </button>
              )}

              {/* Send to Dispatch: When completed products exist */}
              {completedCount > 0 && (
                <button
                  type="button"
                  className="prod-action-btn prod-action-btn-dispatch btn-md"
                  style={{ fontWeight: 600 }}
                  onClick={() => {
                    const completedOrderIds = batch.orders
                      .filter((o) => (optimisticStatus?.[o.order_id]?.production_status || o.production_status) === "Completed")
                      .map((o) => o.order_id);
                    onBatchReadyForDispatch && onBatchReadyForDispatch(batch.batch_id, completedOrderIds);
                  }}
                  disabled={busyOrderIds?.has(batch.batch_id)}
                  title={`Send ${completedCount} completed product(s) to Dispatch`}
                  data-testid={`batch-card-send-dispatch-btn-${batch.batch_id}`}
                >
                  <Send size={11} /> {busyOrderIds?.has(batch.batch_id) ? "Sending..." : `Send to Dispatch (${completedCount})`}
                </button>
              )}
            </>
          )}
          <button
            type="button"
            className="primary-btn compact"
            style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
            onClick={() => onOpenBatch(batch.batch_id)}
            data-testid={`manage-batch-btn-${batch.batch_id}`}
          >
            Manage Production Request <ArrowRight size={13} />
          </button>
        </div>
      </div>

    </div>
  );
}

// -------------------------------------------------------------
// Grouped Vendor Request Card (for Vendor Requests Tab)
// -------------------------------------------------------------
function GroupedVendorRequestCard({ batch, canAct, onSelectOrder, onBatchAssign }) {
  const isUrgent = batch.priority === "Urgent";
  const isHigh = batch.priority === "High";

  const totalQty = (batch.requests || []).reduce((sum, r) => sum + (r.quantity || 0), 0);
  const totalVal = (batch.requests || []).reduce((sum, r) => sum + (r.total_value || 0), 0);

  // Accurate vendor summary across batch
  const assignedVendors = (batch.requests || []).map((r) => r.vendor_name || r.production_vendor_name).filter(Boolean);
  const uniqueVendors = [...new Set(assignedVendors)];
  let batchVendorDisplay = null;
  if (uniqueVendors.length === 1) {
    if (assignedVendors.length === batch.requests.length) {
      batchVendorDisplay = uniqueVendors[0];
    } else {
      batchVendorDisplay = `${uniqueVendors[0]} (${assignedVendors.length}/${batch.requests.length})`;
    }
  } else if (uniqueVendors.length > 1) {
    batchVendorDisplay = `Multiple (${uniqueVendors.length} Vendors)`;
  }

  // Accurate warehouse summary across batch
  const assignedWarehouses = (batch.requests || []).map((r) => r.warehouse_name).filter(Boolean);
  const uniqueWarehouses = [...new Set(assignedWarehouses)];
  let batchWarehouseDisplay = null;
  if (uniqueWarehouses.length === 1) {
    if (assignedWarehouses.length === batch.requests.length) {
      batchWarehouseDisplay = uniqueWarehouses[0];
    } else {
      batchWarehouseDisplay = `${uniqueWarehouses[0]} (${assignedWarehouses.length}/${batch.requests.length})`;
    }
  } else if (uniqueWarehouses.length > 1) {
    batchWarehouseDisplay = `Multiple (${uniqueWarehouses.length} Locations)`;
  }

  const isAllConfirmed = batch.isAllConfirmed;

  return (
    <div
      className="bulk-batch-card panel"
      style={{
        margin: 0,
        background: "#ffffff",
        border: "1.5px solid #cbd5e1",
        borderRadius: 9,
        padding: "16px 18px",
        boxShadow: "0 2px 6px rgba(15, 23, 42, 0.05)",
      }}
      data-testid={`grouped-vendor-request-card-${batch.batch_id}`}
    >
      {/* Top Row: Batch ID, Tag & Status */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            className="mono"
            style={{
              background: "#eff6ff",
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              padding: "2px 8px",
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            #{batch.batch_id}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              background: "#f1f5f9",
              padding: "2px 7px",
              borderRadius: 4,
              border: "1px solid #e2e8f0",
            }}
          >
            Bulk Production Request · {batch.requests.length} Products
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className={`badge ${isUrgent ? "red" : isHigh ? "amber" : "blue"}`} style={{ fontSize: 10 }}>
            {batch.priority}
          </span>
          <span className={`badge ${isAllConfirmed ? "green" : "amber"}`} style={{ fontSize: 11 }}>
            {isAllConfirmed ? "All Details Confirmed" : `${batch.pendingCount} Pending Details`}
          </span>
        </div>
      </div>

      {/* Client Name & Summary Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: "2px 0 2px", fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
            {batch.client_name}
          </h3>
          {batch.client_contact_person && (
            <div style={{ fontSize: 12, color: "#64748b" }}>
              Contact: {batch.client_contact_person}
            </div>
          )}
        </div>

        <div>
          <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Total Quantity</small>
          <b>{totalQty.toLocaleString()} units</b>
          {totalVal > 0 && <small className="muted" style={{ display: "block", fontSize: 11 }}>{compactMoney(totalVal)}</small>}
        </div>

        <div>
          <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Assigned Vendor</small>
          {batchVendorDisplay ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <Factory size={13} color="#4f46e5" />
              <b>{batchVendorDisplay}</b>
            </div>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
          )}
        </div>

        <div>
          <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Assigned Warehouse</small>
          {batchWarehouseDisplay ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <Warehouse size={13} color="#059669" />
              <b>{batchWarehouseDisplay}</b>
            </div>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
          )}
        </div>
      </div>

      {/* Itemized Table */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 6,
          padding: "8px 10px",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 80px 120px 1.2fr 1fr 90px",
            gap: 8,
            fontSize: 10.5,
            fontWeight: 700,
            color: "var(--muted)",
            textTransform: "uppercase",
            borderBottom: "1px solid #e2e8f0",
            paddingBottom: 5,
            marginBottom: 6,
          }}
        >
          <span>Product / Order</span>
          <span style={{ textAlign: "right" }}>Qty</span>
          <span>Design Info</span>
          <span>Vendor</span>
          <span>Warehouse</span>
          <span style={{ textAlign: "right" }}>Action</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {batch.requests.map((r) => {
            const hasVendor = Boolean(r.vendor_name || r.production_vendor_name || r.vendor_id);
            const hasWarehouse = Boolean(r.warehouse_name || r.warehouse_id);
            const isUnassigned = !hasVendor && !hasWarehouse;

            return (
              <div
                key={r.request_id || r.order_id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 80px 120px 1.2fr 1fr 90px",
                  gap: 8,
                  alignItems: "center",
                  fontSize: 12,
                  padding: "4px 0",
                  borderBottom: "1px solid #f1f5f9",
                }}
                data-testid={`batch-request-item-${r.order_id}`}
              >
                <div>
                  <b style={{ color: "var(--navy)" }}>{r.product}</b>
                  <span className="mono" style={{ fontSize: 10.5, color: "#64748b", marginLeft: 6 }}>
                    (#{r.order_id})
                  </span>
                  <div
                    style={{
                      fontSize: 11,
                      color: r.description?.trim() ? "#475569" : "#94a3b8",
                      fontStyle: r.description?.trim() ? "normal" : "italic",
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={r.description?.trim() || "No description provided"}
                  >
                    {r.description?.trim() ? r.description.trim() : "No description provided"}
                  </div>
                </div>

                <div style={{ textAlign: "right", color: "#334155", fontWeight: 600 }}>
                  {(r.quantity || 0).toLocaleString()} pcs
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span className="badge green" style={{ fontSize: 9.5 }}>Approved</span>
                  {r.design_version > 0 && <span className="badge purple" style={{ fontSize: 9.5 }}>V{r.design_version}</span>}
                </div>

                <div>
                  {r.vendor_name || r.production_vendor_name ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5 }}>
                      <Factory size={12} color="#4f46e5" />
                      <b>{r.vendor_name || r.production_vendor_name}</b>
                    </div>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 11.5 }}>—</span>
                  )}
                </div>

                <div>
                  {r.warehouse_name ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5 }}>
                      <Warehouse size={12} color="#059669" />
                      <b>{r.warehouse_name}</b>
                    </div>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 11.5 }}>—</span>
                  )}
                </div>

                <div style={{ textAlign: "right" }}>
                  <button
                    type="button"
                    className={isUnassigned ? "primary-btn compact" : "outline-btn compact"}
                    onClick={() => onSelectOrder(r)}
                    style={{ padding: "2px 7px", fontSize: 11 }}
                    data-testid={`assign-individual-btn-${r.order_id}`}
                  >
                    {isUnassigned ? "Assign" : "Update"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Card Footer */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 12, color: "#64748b" }}>
          Requested by {batch.requested_by || "Design Team"} on{" "}
          {batch.requested_at ? new Date(batch.requested_at).toLocaleDateString() : "—"}
        </div>

        {canAct && (
          <button
            type="button"
            className="primary-btn compact"
            style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
            onClick={() => onBatchAssign(batch)}
            data-testid={`batch-assign-details-btn-${batch.batch_id}`}
          >
            <Factory size={13} /> {isAllConfirmed ? "Update Details (Batch)" : "Assign Details (Batch)"}
          </button>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Batch Vendor Request Modal (for Vendor Requests Tab)
// -------------------------------------------------------------
function BatchVendorRequestModal({ batch, vendors, warehouses, canAct, onClose, onSuccess }) {
  const allSameVendor = batch.requests.length > 0 && batch.requests.every(r => (r.vendor_name || r.production_vendor_name) && (r.vendor_name || r.production_vendor_name) === (batch.requests[0].vendor_name || batch.requests[0].production_vendor_name));
  const initialVendorName = allSameVendor ? (batch.requests[0].vendor_name || batch.requests[0].production_vendor_name) : "";
  const initialVendor = vendors.find((v) => (v.name || v.vendor_name) === initialVendorName || v.vendor_id === batch.requests[0]?.vendor_id);

  const allSameWarehouse = batch.requests.length > 0 && batch.requests.every(r => r.warehouse_name && r.warehouse_name === batch.requests[0].warehouse_name);
  const initialWarehouseName = allSameWarehouse ? batch.requests[0].warehouse_name : "";
  const initialWarehouse = warehouses.find((w) => (w.name || w.warehouse_name) === initialWarehouseName || w.warehouse_id === batch.requests[0]?.warehouse_id);

  const [selectedVendorId, setSelectedVendorId] = useState(initialVendor?.vendor_id || "");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(initialWarehouse?.warehouse_id || "");
  const [selectedIds, setSelectedIds] = useState(batch.requests.map((r) => r.order_id));
  const [submitting, setSubmitting] = useState(false);

  const toggleOne = (oid) => {
    if (selectedIds.includes(oid)) {
      setSelectedIds(selectedIds.filter((x) => x !== oid));
    } else {
      setSelectedIds([...selectedIds, oid]);
    }
  };

  const toggleAll = () => {
    if (selectedIds.length === batch.requests.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(batch.requests.map((r) => r.order_id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVendorId && !selectedWarehouseId) {
      toast.error("Select a Vendor or Warehouse to continue.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select at least one order.");
      return;
    }

    const v = vendors.find((x) => x.vendor_id === selectedVendorId);
    const w = warehouses.find((x) => x.warehouse_id === selectedWarehouseId);

    setSubmitting(true);
    try {
      await api("post", `/orders/batch/${batch.batch_id}/confirm-production-details`, {
        order_ids: selectedIds,
        vendor_id: selectedVendorId || "",
        vendor_name: v?.name || v?.vendor_name || "",
        warehouse_id: selectedWarehouseId || "",
        warehouse_name: w?.name || w?.warehouse_name || "",
      });

      toast.success(`Production details confirmed for Bulk Request #${batch.batch_id}`);
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to confirm batch production details";
      toast.error(typeof msg === "string" ? msg : "Confirmation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 540, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label">BATCH VENDOR ASSIGNMENT</div>
            <h3>Assign Details for Batch #{batch.batch_id}</h3>
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              {batch.client_name} · {batch.requests.length} Orders
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">Production Vendor</label>
            <select
              className="dispatch-control"
              value={selectedVendorId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedVendorId(val);
                if (val) setSelectedWarehouseId("");
              }}
              data-testid="batch-req-modal-vendor-select"
            >
              <option value="">-- Select Active Vendor (Optional) --</option>
              {vendors.length === 0 && (
                <option value="" disabled>No active vendors available in Production Resources</option>
              )}
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>
                  {v.name || v.vendor_name} {v.contact_person ? `(${v.contact_person})` : (v.location ? `· ${v.location}` : "")}
                </option>
              ))}
            </select>
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Procurement Warehouse</label>
            <select
              className="dispatch-control"
              value={selectedWarehouseId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedWarehouseId(val);
                if (val) setSelectedVendorId("");
              }}
              data-testid="batch-req-modal-warehouse-select"
            >
              <option value="">-- Select Active Warehouse (Optional) --</option>
              {warehouses.length === 0 && (
                <option value="" disabled>No active warehouses available in Production Resources</option>
              )}
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id}>
                  {w.name || w.warehouse_name} {w.location || w.address ? `· ${(w.location || w.address).slice(0, 30)}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label className="dispatch-label" style={{ margin: 0 }}>
                Apply to Orders ({selectedIds.length}/{batch.requests.length})
              </label>
              <button
                type="button"
                onClick={toggleAll}
                style={{ background: "none", border: "none", color: "#2563eb", fontSize: 11.5, cursor: "pointer", fontWeight: 600, padding: 0 }}
              >
                {selectedIds.length === batch.requests.length ? "Deselect All" : "Select All"}
              </button>
            </div>

            <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 8px" }}>
              {batch.requests.map((r) => (
                <label
                  key={r.order_id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 6px",
                    fontSize: 12.5,
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(r.order_id)}
                    onChange={() => toggleOne(r.order_id)}
                  />
                  <span>
                    <strong>{r.product}</strong> (#{r.order_id}) — {r.quantity} pcs
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={submitting || (!selectedVendorId && !selectedWarehouseId)}
              data-testid="batch-confirm-vendor-details-btn"
            >
              {submitting ? "Confirming..." : `Assign Details (${selectedIds.length})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Single Production Request Detail Modal
// -------------------------------------------------------------
function ProductionRequestModal({ request, vendors, warehouses, canAct, onClose, onSuccess }) {
  const [selectedVendorId, setSelectedVendorId] = useState(request.vendor_id || request.production_vendor_id || "");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(request.warehouse_id || "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (request) {
      let vId = request.vendor_id || request.production_vendor_id || "";
      if (!vId && (request.vendor_name || request.production_vendor_name) && vendors.length > 0) {
        const reqVName = (request.vendor_name || request.production_vendor_name).toLowerCase().trim();
        const match = vendors.find((v) => (v.name || v.vendor_name || "").toLowerCase().trim() === reqVName);
        if (match) vId = match.vendor_id;
      }

      let wId = request.warehouse_id || "";
      if (!wId && request.warehouse_name && warehouses.length > 0) {
        const reqWName = request.warehouse_name.toLowerCase().trim();
        const match = warehouses.find((w) => (w.name || w.warehouse_name || "").toLowerCase().trim() === reqWName);
        if (match) wId = match.warehouse_id;
      }

      if (wId && !vId) {
        setSelectedWarehouseId(wId);
        setSelectedVendorId("");
      } else if (vId) {
        setSelectedVendorId(vId);
        setSelectedWarehouseId("");
      } else {
        setSelectedVendorId("");
        setSelectedWarehouseId("");
      }
    }
  }, [request, vendors, warehouses]);

  const hasAtLeastOne = Boolean(selectedVendorId || selectedWarehouseId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasAtLeastOne) {
      toast.error("Select a Vendor or Warehouse to continue.");
      return;
    }

    const v = vendors.find((x) => x.vendor_id === selectedVendorId);
    const w = warehouses.find((x) => x.warehouse_id === selectedWarehouseId);

    setSubmitting(true);
    try {
      await api("post", `/orders/${request.order_id}/confirm-production-details`, {
        vendor_id: selectedVendorId || "",
        vendor_name: v?.name || v?.vendor_name || "",
        warehouse_id: selectedWarehouseId || "",
        warehouse_name: w?.name || w?.warehouse_name || "",
      });

      toast.success(`Production details confirmed for Order #${request.order_id}`);
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not confirm production details";
      toast.error(typeof msg === "string" ? msg : "Confirmation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const docs = request.documents || [];

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div
        className="modal"
        style={{ maxWidth: 580, width: "95%" }}
        data-testid="production-request-detail-modal"
      >
        <div className="modal-head">
          <div>
            <div className="small-label">PRODUCTION WORKFLOW</div>
            <h2>Production Request Details</h2>
            <p className="muted">
              Order #{request.order_id} · Requested by {request.requested_by || "Design Team"} on{" "}
              {request.requested_at ? new Date(request.requested_at).toLocaleDateString() : "—"}
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "18px 24px" }}>
          <div style={{ marginBottom: 16 }}>
            <div className="small-label" style={{ marginBottom: 6 }}>
              ORDER INFORMATION
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px 16px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                padding: "10px 14px",
                fontSize: 12.5,
              }}
            >
              <div>
                <span className="muted" style={{ display: "block", fontSize: 11 }}>Client</span>
                <b>{request.client_name}</b>
              </div>
              <div>
                <span className="muted" style={{ display: "block", fontSize: 11 }}>Product</span>
                <b>{request.product}</b>
              </div>
              <div>
                <span className="muted" style={{ display: "block", fontSize: 11 }}>Quantity</span>
                <b style={{ color: "#4f46e5" }}>{(request.quantity || 0).toLocaleString()} units</b>
              </div>
              <div>
                <span className="muted" style={{ display: "block", fontSize: 11 }}>Delivery Deadline</span>
                <span>{request.required_delivery_date || "—"}</span>
              </div>
              <div>
                <span className="muted" style={{ display: "block", fontSize: 11 }}>Order Value</span>
                <span>{money(request.total_value)}</span>
              </div>
              <div>
                <span className="muted" style={{ display: "block", fontSize: 11 }}>Priority</span>
                <span className={`badge ${request.priority === "Urgent" ? "red" : request.priority === "High" ? "amber" : "blue"}`} style={{ fontSize: 10, padding: "1px 6px" }}>
                  {request.priority || "Normal"}
                </span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div className="small-label" style={{ marginBottom: 6 }}>
              DESIGN INFORMATION
            </div>
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 6,
                padding: "10px 12px",
                fontSize: 12.5,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#166534", fontWeight: 600, fontSize: 12 }}>
                  Design Approved {request.design_version > 0 ? `(V${request.design_version})` : ""}
                </span>
                <span style={{ color: "#15803d", fontSize: 11 }}>
                  Approved by: {request.approved_by || "Authorized Approver"}
                </span>
              </div>

              {request.design_specs && (
                <div style={{ marginTop: 6, color: "#334155", fontSize: 12, lineHeight: 1.4 }}>
                  <span style={{ fontWeight: 600, color: "#166534" }}>Specs: </span>
                  {request.design_specs}
                </div>
              )}

              {docs.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {docs.slice(0, 3).map((d) => (
                    <a
                      key={d.document_id}
                      href={downloadDocument(d.document_id)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        background: "#ffffff",
                        padding: "3px 8px",
                        borderRadius: 4,
                        border: "1px solid #cbd5e1",
                        fontSize: 11.5,
                        color: "#0284c7",
                        textDecoration: "none",
                        fontWeight: 500,
                      }}
                    >
                      <Download size={11} /> {d.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div className="small-label" style={{ marginBottom: 4 }}>
              PRODUCTION ASSIGNMENT
            </div>
            <p className="muted" style={{ margin: "0 0 10px", fontSize: 12 }}>
              Select a Production Vendor or Warehouse location.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#1e293b", marginBottom: 4 }}>
                  Production Vendor <span style={{ color: "#64748b", fontWeight: 400 }}>(Optional)</span>
                </label>
                <select
                  style={{ width: "100%", background: "#fff", border: "1px solid #cbd5e1", padding: "8px 10px", borderRadius: 6, fontSize: 12.5 }}
                  value={selectedVendorId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedVendorId(val);
                    if (val) setSelectedWarehouseId("");
                  }}
                  data-testid="request-modal-select-vendor"
                >
                  <option value="">-- Select Active Vendor (Optional) --</option>
                  {vendors.length === 0 && (
                    <option value="" disabled>No active vendors available in Production Resources</option>
                  )}
                  {vendors.map((v) => (
                    <option key={v.vendor_id} value={v.vendor_id}>
                      {v.name || v.vendor_name} {v.contact_person ? `(${v.contact_person})` : (v.location ? `· ${v.location}` : "")}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#1e293b", marginBottom: 4 }}>
                  Warehouse / Procurement Location <span style={{ color: "#64748b", fontWeight: 400 }}>(Optional)</span>
                </label>
                <select
                  style={{ width: "100%", background: "#fff", border: "1px solid #cbd5e1", padding: "8px 10px", borderRadius: 6, fontSize: 12.5 }}
                  value={selectedWarehouseId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedWarehouseId(val);
                    if (val) setSelectedVendorId("");
                  }}
                  data-testid="request-modal-select-warehouse"
                >
                  <option value="">-- Select Active Warehouse (Optional) --</option>
                  {warehouses.length === 0 && (
                    <option value="" disabled>No active warehouses available in Production Resources</option>
                  )}
                  {warehouses.map((w) => (
                    <option key={w.warehouse_id} value={w.warehouse_id}>
                      {w.name || w.warehouse_name} {w.location || w.address ? `· ${(w.location || w.address).slice(0, 30)}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {!hasAtLeastOne && (
              <div
                style={{
                  fontSize: 11.5,
                  color: "#d97706",
                  marginTop: 8,
                  fontWeight: 500,
                }}
                data-testid="validation-warning"
              >
                Select a Vendor or Warehouse to continue.
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 18,
              paddingTop: 14,
              borderTop: "1px solid var(--line)",
            }}
          >
            <button
              type="button"
              className="outline-btn"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            {canAct && (
              <button
                type="submit"
                className="primary-btn"
                disabled={submitting || !hasAtLeastOne}
                data-testid="confirm-production-details-btn"
              >
                {submitting ? "Confirming..." : "Confirm Production Details"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Batch Assign Vendor & Warehouse Modal
// -------------------------------------------------------------
function BatchAssignModal({ batch, vendors, warehouses, onClose, onSave }) {
  const [vendorId, setVendorId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [selectedIds, setSelectedIds] = useState(batch.orders.map((o) => o.order_id));

  const toggleAll = () => {
    if (selectedIds.length === batch.orders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(batch.orders.map((o) => o.order_id));
    }
  };

  const toggleOne = (oid) => {
    if (selectedIds.includes(oid)) {
      setSelectedIds(selectedIds.filter((x) => x !== oid));
    } else {
      setSelectedIds([...selectedIds, oid]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!vendorId && !warehouseId) {
      toast.error("Please select a Vendor or Warehouse.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select at least one product.");
      return;
    }
    const v = vendors.find((x) => x.vendor_id === vendorId);
    const w = warehouses.find((x) => x.warehouse_id === warehouseId);
    onSave({
      vendorId,
      vendorName: v?.name || v?.vendor_name || "",
      warehouseId,
      warehouseName: w?.name || w?.warehouse_name || "",
      orderIds: selectedIds,
    });
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 540, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label">BULK ASSIGNMENT</div>
            <h3>Assign Vendor &amp; Warehouse (Batch #{batch.batch_id})</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">Production Vendor</label>
            <select
              className="dispatch-control"
              value={vendorId}
              onChange={(e) => {
                const val = e.target.value;
                setVendorId(val);
                if (val) setWarehouseId("");
              }}
              data-testid="batch-assign-vendor-select"
            >
              <option value="">-- Select Active Vendor (Optional) --</option>
              {vendors.length === 0 && (
                <option value="" disabled>No active vendors available in Production Resources</option>
              )}
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>
                  {v.name || v.vendor_name} {v.contact_person ? `(${v.contact_person})` : (v.location ? `· ${v.location}` : "")}
                </option>
              ))}
            </select>
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Procurement Warehouse</label>
            <select
              className="dispatch-control"
              value={warehouseId}
              onChange={(e) => {
                const val = e.target.value;
                setWarehouseId(val);
                if (val) setVendorId("");
              }}
              data-testid="batch-assign-warehouse-select"
            >
              <option value="">-- Select Active Warehouse (Optional) --</option>
              {warehouses.length === 0 && (
                <option value="" disabled>No active warehouses available in Production Resources</option>
              )}
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id}>
                  {w.name || w.warehouse_name} {w.location || w.address ? `· ${(w.location || w.address).slice(0, 30)}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label className="dispatch-label" style={{ margin: 0 }}>
                Apply to Products ({selectedIds.length}/{batch.orders.length})
              </label>
              <button type="button" className="outline-btn compact" onClick={toggleAll} style={{ padding: "2px 6px", fontSize: 11 }}>
                {selectedIds.length === batch.orders.length ? "Deselect All" : "Select All"}
              </button>
            </div>

            <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 8px" }}>
              {batch.orders.map((o) => (
                <label
                  key={o.order_id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 6px",
                    fontSize: 12.5,
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(o.order_id)}
                    onChange={() => toggleOne(o.order_id)}
                  />
                  <span>
                    <strong>{o.product}</strong> (#{o.order_id}) — {o.quantity} {o.unit || "pcs"}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" data-testid="batch-save-assignment-btn">
              Apply to Selected ({selectedIds.length})
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Quick Start Single Order Modal (Requires Vendor or Warehouse)
// -------------------------------------------------------------
function QuickStartProductionModal({ order, vendors, warehouses, onClose, onStarted }) {
  const [assignmentType, setAssignmentType] = useState("vendor"); // "vendor" | "warehouse"
  const [vendorId, setVendorId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [startDate, setStartDate] = useState(getTodayDateString());
  const [estDate, setEstDate] = useState(order.required_delivery_date || "");
  const [remarks, setRemarks] = useState("Production started directly from order list");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (assignmentType === "vendor" && !vendorId) {
      toast.error("Please select a Production Vendor before starting.");
      return;
    }
    if (assignmentType === "warehouse" && !warehouseId) {
      toast.error("Please select a Procurement Warehouse before starting.");
      return;
    }

    const v = vendors.find((x) => x.vendor_id === vendorId);
    const w = warehouses.find((x) => x.warehouse_id === warehouseId);

    setSubmitting(true);
    try {
      await api("patch", `/orders/${order.order_id}/production`, {
        status: "In Progress",
        vendor_id: assignmentType === "vendor" ? vendorId : "",
        vendor_name: assignmentType === "vendor" ? (v?.name || v?.vendor_name || "") : "",
        warehouse_id: assignmentType === "warehouse" ? warehouseId : "",
        warehouse_name: assignmentType === "warehouse" ? (w?.name || w?.warehouse_name || "") : "",
        start_date: startDate || getTodayDateString(),
        expected_completion: estDate || "",
        estimated_ready_date: estDate || "",
        remarks: remarks || "",
      });
      toast.success(`Production started for #${order.order_id}`);
      onStarted({
        ...order,
        production_status: "In Progress",
        production_vendor_id: assignmentType === "vendor" ? vendorId : "",
        production_vendor_name: assignmentType === "vendor" ? (v?.name || v?.vendor_name || "") : "",
        warehouse_id: assignmentType === "warehouse" ? warehouseId : "",
        warehouse_name: assignmentType === "warehouse" ? (w?.name || w?.warehouse_name || "") : "",
        production_start_date: startDate || getTodayDateString(),
        estimated_ready_date: estDate || "",
      });
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not start production";
      toast.error(typeof msg === "string" ? msg : "Start failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 520, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label">START PRODUCTION</div>
            <h3>Start Production — #{order.order_id}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          {/* Informational Callout */}
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 6,
              padding: "10px 12px",
              marginBottom: 16,
              fontSize: 12.5,
              color: "#92400e",
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <strong>Vendor or Warehouse Required</strong>
              <div style={{ marginTop: 2, color: "#78350f", lineHeight: 1.4 }}>
                This order is not assigned to a production vendor or warehouse facility yet. Please assign one below to start production immediately.
              </div>
            </div>
          </div>

          {/* Order Snapshot */}
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              padding: "10px 12px",
              marginBottom: 16,
              fontSize: 12,
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <span className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Client</span>
                <strong style={{ color: "var(--navy)" }}>{order.client_name}</strong>
              </div>
              <div>
                <span className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Product</span>
                <strong style={{ color: "var(--navy)" }}>{order.product}</strong>
              </div>
              <div>
                <span className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Quantity</span>
                <span>{(order.quantity || 0).toLocaleString()} {order.unit || "units"}</span>
              </div>
              <div>
                <span className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Due Date</span>
                <span>{order.required_delivery_date || "Not set"}</span>
              </div>
            </div>
          </div>

          {/* Assignment Type Selector */}
          <div className="dispatch-field">
            <label className="dispatch-label">Production Assignment</label>
            <div style={{ display: "flex", gap: 14, marginTop: 4, marginBottom: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                <input
                  type="radio"
                  name="assignType"
                  value="vendor"
                  checked={assignmentType === "vendor"}
                  onChange={() => setAssignmentType("vendor")}
                />
                <Factory size={13} color="#4f46e5" />
                <span>External Vendor</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                <input
                  type="radio"
                  name="assignType"
                  value="warehouse"
                  checked={assignmentType === "warehouse"}
                  onChange={() => setAssignmentType("warehouse")}
                />
                <Warehouse size={13} color="#0284c7" />
                <span>Internal Warehouse</span>
              </label>
            </div>

            {assignmentType === "vendor" ? (
              <select
                className="dispatch-control"
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                required
                data-testid="quick-start-vendor-select"
              >
                <option value="">-- Select Production Vendor * --</option>
                {vendors.map((v) => (
                  <option key={v.vendor_id} value={v.vendor_id}>
                    {v.name || v.vendor_name} {v.contact_person ? `(${v.contact_person})` : (v.location ? `· ${v.location}` : "")}
                  </option>
                ))}
              </select>
            ) : (
              <select
                className="dispatch-control"
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                required
                data-testid="quick-start-warehouse-select"
              >
                <option value="">-- Select Procurement Warehouse * --</option>
                {warehouses.map((w) => (
                  <option key={w.warehouse_id} value={w.warehouse_id}>
                    {w.name || w.warehouse_name} {w.location || w.address ? `· ${(w.location || w.address).slice(0, 30)}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Dates & Remarks */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            <div className="dispatch-field">
              <label className="dispatch-label">Production Start Date</label>
              <input
                type="date"
                className="dispatch-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="dispatch-field">
              <label className="dispatch-label">Est. Ready Date (Optional)</label>
              <input
                type="date"
                className="dispatch-control"
                value={estDate}
                onChange={(e) => setEstDate(e.target.value)}
              />
            </div>
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Production Remarks (Optional)</label>
            <input
              type="text"
              className="dispatch-control"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Started directly from order list"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#16a34a", borderColor: "#16a34a" }}
              disabled={submitting}
              data-testid="quick-start-submit-btn"
            >
              <Play size={13} fill="currentColor" />
              {submitting ? "Starting..." : "Assign & Start Production"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Batch Start Production Modal (for BulkProductionBatchCard)
// -------------------------------------------------------------
function BatchStartModal({ batch, vendors, warehouses, onClose, onStarted }) {
  const notStartedOrders = useMemo(() => {
    return (batch.orders || []).filter((o) => (o.production_status || "Not Started") === "Not Started");
  }, [batch]);

  const unassignedCount = useMemo(() => {
    return notStartedOrders.filter(
      (o) => !o.production_vendor_id && !o.production_vendor_name && !o.assigned_producer && !o.warehouse_id && !o.warehouse_name
    ).length;
  }, [notStartedOrders]);

  const [selectedIds, setSelectedIds] = useState(notStartedOrders.map((o) => o.order_id));
  const [defaultVendorId, setDefaultVendorId] = useState("");
  const [defaultWarehouseId, setDefaultWarehouseId] = useState("");
  const [startDate, setStartDate] = useState(getTodayDateString());
  const [submitting, setSubmitting] = useState(false);

  const toggleAll = () => {
    if (selectedIds.length === notStartedOrders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(notStartedOrders.map((o) => o.order_id));
    }
  };

  const toggleOne = (oid) => {
    if (selectedIds.includes(oid)) {
      setSelectedIds(selectedIds.filter((x) => x !== oid));
    } else {
      setSelectedIds([...selectedIds, oid]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) {
      toast.error("Please select at least one order to start.");
      return;
    }

    const v = vendors.find((x) => x.vendor_id === defaultVendorId);
    const w = warehouses.find((x) => x.warehouse_id === defaultWarehouseId);

    setSubmitting(true);
    try {
      const res = await api("patch", `/orders/batch/${batch.batch_id}/production`, {
        order_ids: selectedIds,
        status: "In Progress",
        start_date: startDate || getTodayDateString(),
        vendor_id: defaultVendorId || undefined,
        vendor_name: v?.name || v?.vendor_name || undefined,
        warehouse_id: defaultWarehouseId || undefined,
        warehouse_name: w?.name || w?.warehouse_name || undefined,
        remarks: "Started from Production list batch action",
      });
      const count = res.data?.updated_count || selectedIds.length;
      toast.success(`Successfully started ${count} order(s) in production!`);
      onStarted && onStarted(selectedIds, startDate || getTodayDateString());
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not start batch production";
      toast.error(typeof msg === "string" ? msg : "Batch start failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 560, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label">BULK PRODUCTION START</div>
            <h3>Start Production — Batch #{batch.batch_id}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: "#334155" }}>
              Starting production for <strong>{batch.client_name}</strong> ({selectedIds.length} orders selected)
            </div>
          </div>

          {unassignedCount > 0 && (
            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 6,
                padding: "10px 12px",
                marginBottom: 14,
                fontSize: 12,
                color: "#92400e",
              }}
            >
              <div style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 700 }}>
                <AlertCircle size={14} /> {unassignedCount} order(s) are missing vendor/warehouse assignment
              </div>
              <div style={{ marginTop: 4, color: "#78350f" }}>
                Select a default Vendor or Warehouse below to automatically assign and start them:
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                <select
                  className="dispatch-control"
                  value={defaultVendorId}
                  onChange={(e) => {
                    setDefaultVendorId(e.target.value);
                    if (e.target.value) setDefaultWarehouseId("");
                  }}
                  style={{ fontSize: 12 }}
                >
                  <option value="">-- Assign Vendor --</option>
                  {vendors.map((v) => (
                    <option key={v.vendor_id} value={v.vendor_id}>
                      {v.name || v.vendor_name}
                    </option>
                  ))}
                </select>
                <select
                  className="dispatch-control"
                  value={defaultWarehouseId}
                  onChange={(e) => {
                    setDefaultWarehouseId(e.target.value);
                    if (e.target.value) setDefaultVendorId("");
                  }}
                  style={{ fontSize: 12 }}
                >
                  <option value="">-- Assign Warehouse --</option>
                  {warehouses.map((w) => (
                    <option key={w.warehouse_id} value={w.warehouse_id}>
                      {w.name || w.warehouse_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="dispatch-field">
            <label className="dispatch-label">Production Start Date</label>
            <input
              type="date"
              className="dispatch-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label className="dispatch-label" style={{ margin: 0 }}>
                Select Orders to Start ({selectedIds.length}/{notStartedOrders.length})
              </label>
              <button type="button" className="outline-btn compact" onClick={toggleAll} style={{ padding: "2px 6px", fontSize: 11 }}>
                {selectedIds.length === notStartedOrders.length ? "Deselect All" : "Select All"}
              </button>
            </div>

            <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 8px" }}>
              {notStartedOrders.map((o) => {
                const isAssigned = Boolean(o.production_vendor_name || o.assigned_producer || o.warehouse_name);
                return (
                  <label
                    key={o.order_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "4px 6px",
                      fontSize: 12,
                      cursor: "pointer",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(o.order_id)}
                      onChange={() => toggleOne(o.order_id)}
                    />
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <strong>{o.product}</strong> (#{o.order_id}) — {o.quantity} {o.unit || "pcs"}
                    </span>
                    {isAssigned ? (
                      <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>Ready</span>
                    ) : (
                      <span style={{ fontSize: 10.5, color: "#d97706", fontWeight: 600 }}>Unassigned</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#16a34a", borderColor: "#16a34a" }}
              disabled={submitting || selectedIds.length === 0}
              data-testid="batch-confirm-start-btn"
            >
              <Play size={13} fill="currentColor" />
              {submitting ? "Starting..." : `Start Production (${selectedIds.length})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Batch Hold Production Modal (for BulkProductionBatchCard)
// -------------------------------------------------------------
function BatchHoldModal({ batch, optimisticStatus, onClose, onConfirm }) {
  const eligibleOrders = useMemo(() => {
    return (batch.orders || []).filter(
      (o) => (optimisticStatus?.[o.order_id]?.production_status || o.production_status) === "In Progress"
    );
  }, [batch, optimisticStatus]);

  const [holdReason, setHoldReason] = useState("");
  const [nextEstDate, setNextEstDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const totalBatchProducts = batch.orders?.length || 0;
  const otherCount = totalBatchProducts - eligibleOrders.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!holdReason.trim()) {
      toast.error("Please provide a reason for placing production on hold.");
      return;
    }
    if (eligibleOrders.length === 0) {
      toast.error("No eligible In Progress products to put on hold.");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({
        batchId: batch.batch_id,
        orderIds: eligibleOrders.map((o) => o.order_id),
        holdReason: holdReason.trim(),
        nextEstDate: nextEstDate || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 540, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#d97706" }}>BULK HOLD TRANSITION</div>
            <h3>Hold All Production — #{batch.batch_id}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} disabled={submitting}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          {/* Confirmation Banner */}
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 6,
              padding: "12px 14px",
              marginBottom: 16,
              fontSize: 12.5,
              color: "#92400e",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
              <AlertCircle size={15} color="#d97706" />
              <span>Confirm Bulk Hold for {eligibleOrders.length} Product(s)</span>
            </div>
            <div>
              Placing on hold will pause production for <strong>{eligibleOrders.length} In Progress</strong> product(s) in batch <strong>#{batch.batch_id}</strong> ({batch.client_name}).
              {otherCount > 0 && (
                <div style={{ marginTop: 4, fontSize: 11.5, color: "#b45309" }}>
                  Note: {otherCount} other product(s) in this batch (Not Started / Completed / already On Hold) will maintain their current status.
                </div>
              )}
            </div>
          </div>

          {/* List of Affected Products */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", marginBottom: 6 }}>
              Affected Products ({eligibleOrders.length}):
            </div>
            <div
              style={{
                maxHeight: 140,
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                background: "#f8fafc",
                padding: "4px 8px",
              }}
            >
              {eligibleOrders.map((o) => (
                <div
                  key={o.order_id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "5px 4px",
                    borderBottom: "1px solid #f1f5f9",
                    fontSize: 12,
                  }}
                >
                  <div>
                    <strong style={{ color: "var(--navy)" }}>{o.product}</strong>
                    <span className="mono" style={{ color: "#64748b", marginLeft: 6, fontSize: 11 }}>
                      (#{o.order_id})
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: "#334155" }}>{o.quantity} {o.unit || "pcs"}</span>
                    <span className="badge purple" style={{ fontSize: 10 }}>In Progress</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="dispatch-field">
            <label className="dispatch-label">Reason for Hold *</label>
            <textarea
              className="dispatch-control"
              rows={3}
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder="e.g. Awaiting client material confirmation, dye shortage, shop-floor inspection..."
              required
              autoFocus
              data-testid="batch-hold-reason-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Revised Expected Ready Date (Optional)</label>
            <input
              type="date"
              className="dispatch-control"
              value={nextEstDate}
              onChange={(e) => setNextEstDate(e.target.value)}
              data-testid="batch-hold-revised-date-input"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#d97706", borderColor: "#d97706", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}
              disabled={submitting || !holdReason.trim() || eligibleOrders.length === 0}
              data-testid="confirm-batch-hold-btn"
            >
              <Pause size={12} />
              <span>{submitting ? "Placing On Hold..." : `Confirm Hold All (${eligibleOrders.length})`}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Batch Resume Production Modal (for BulkProductionBatchCard)
// -------------------------------------------------------------
function BatchResumeModal({ batch, optimisticStatus, onClose, onConfirm }) {
  const eligibleOrders = useMemo(() => {
    return (batch.orders || []).filter(
      (o) => (optimisticStatus?.[o.order_id]?.production_status || o.production_status) === "On Hold"
    );
  }, [batch, optimisticStatus]);

  const [submitting, setSubmitting] = useState(false);
  const totalBatchProducts = batch.orders?.length || 0;
  const otherCount = totalBatchProducts - eligibleOrders.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (eligibleOrders.length === 0) {
      toast.error("No eligible On Hold products to resume.");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({
        batchId: batch.batch_id,
        orderIds: eligibleOrders.map((o) => o.order_id),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 540, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#7c3aed" }}>BULK RESUME TRANSITION</div>
            <h3>Resume Production — #{batch.batch_id}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} disabled={submitting}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          {/* Confirmation Banner */}
          <div
            style={{
              background: "#f5f3ff",
              border: "1px solid #ddd6fe",
              borderRadius: 6,
              padding: "12px 14px",
              marginBottom: 16,
              fontSize: 12.5,
              color: "#5b21b6",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
              <Play size={15} fill="currentColor" color="#7c3aed" />
              <span>Resume Production for {eligibleOrders.length} Product(s)</span>
            </div>
            <div>
              Resuming production will move <strong>{eligibleOrders.length} On Hold</strong> product(s) back to <strong>In Progress</strong> for <strong>#{batch.batch_id}</strong> ({batch.client_name}).
              {otherCount > 0 && (
                <div style={{ marginTop: 4, fontSize: 11.5, color: "#6d28d9" }}>
                  Note: {otherCount} other product(s) in this batch will maintain their current status.
                </div>
              )}
            </div>
          </div>

          {/* List of Affected Products */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", marginBottom: 6 }}>
              Products to Resume ({eligibleOrders.length}):
            </div>
            <div
              style={{
                maxHeight: 160,
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                background: "#f8fafc",
                padding: "4px 8px",
              }}
            >
              {eligibleOrders.map((o) => (
                <div
                  key={o.order_id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "5px 4px",
                    borderBottom: "1px solid #f1f5f9",
                    fontSize: 12,
                  }}
                >
                  <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                    <strong style={{ color: "var(--navy)" }}>{o.product}</strong>
                    <span className="mono" style={{ color: "#64748b", marginLeft: 6, fontSize: 11 }}>
                      (#{o.order_id})
                    </span>
                    {(o.hold_reason || o.production_remarks) && (
                      <div style={{ fontSize: 11, color: "#b45309", marginTop: 2 }}>
                        Hold reason: {o.hold_reason || o.production_remarks}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: "#334155" }}>{o.quantity} {o.unit || "pcs"}</span>
                    <span className="badge amber" style={{ fontSize: 10 }}>On Hold</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#7c3aed", borderColor: "#7c3aed", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}
              disabled={submitting || eligibleOrders.length === 0}
              data-testid="confirm-batch-resume-btn"
            >
              <Play size={12} fill="currentColor" />
              <span>{submitting ? "Resuming..." : `Confirm Resume Production (${eligibleOrders.length})`}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Batch Complete Production Modal (for BulkProductionBatchCard)
// -------------------------------------------------------------
function BatchCompleteModal({ batch, optimisticStatus, onClose, onConfirm }) {
  const eligibleOrders = useMemo(() => {
    return (batch.orders || []).filter(
      (o) => (optimisticStatus?.[o.order_id]?.production_status || o.production_status) === "In Progress"
    );
  }, [batch, optimisticStatus]);

  const [actualDate, setActualDate] = useState(getTodayDateString());
  const [remarks, setRemarks] = useState("Production completed directly from order list batch action");
  const [submitting, setSubmitting] = useState(false);

  const totalBatchProducts = batch.orders?.length || 0;
  const otherCount = totalBatchProducts - eligibleOrders.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (eligibleOrders.length === 0) {
      toast.error("No eligible In Progress products to complete.");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({
        batchId: batch.batch_id,
        orderIds: eligibleOrders.map((o) => o.order_id),
        actualDate: actualDate || getTodayDateString(),
        remarks: remarks || "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 540, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#16a34a" }}>BULK COMPLETE TRANSITION</div>
            <h3>Complete All Production — #{batch.batch_id}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} disabled={submitting}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          {/* Confirmation Banner */}
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 6,
              padding: "12px 14px",
              marginBottom: 16,
              fontSize: 12.5,
              color: "#166534",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
              <CheckCircle2 size={15} color="#16a34a" />
              <span>Confirm Bulk Complete for {eligibleOrders.length} Product(s)</span>
            </div>
            <div>
              Marking as completed will finish production for <strong>{eligibleOrders.length} In Progress</strong> product(s) in batch <strong>#{batch.batch_id}</strong> ({batch.client_name}).
              <div style={{ marginTop: 4, fontSize: 11.5, color: "#15803d" }}>
                Completed products remain in the Production queue until sent to Dispatch via "Send to Dispatch".
              </div>
              {otherCount > 0 && (
                <div style={{ marginTop: 4, fontSize: 11.5, color: "#15803d" }}>
                  Note: {otherCount} other product(s) in this batch (Not Started / On Hold / already Completed) will maintain their current status.
                </div>
              )}
            </div>
          </div>

          {/* List of Affected Products */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", marginBottom: 6 }}>
              Products Being Completed ({eligibleOrders.length}):
            </div>
            <div
              style={{
                maxHeight: 140,
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                background: "#f8fafc",
                padding: "4px 8px",
              }}
            >
              {eligibleOrders.map((o) => (
                <div
                  key={o.order_id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "5px 4px",
                    borderBottom: "1px solid #f1f5f9",
                    fontSize: 12,
                  }}
                >
                  <div>
                    <strong style={{ color: "var(--navy)" }}>{o.product}</strong>
                    <span className="mono" style={{ color: "#64748b", marginLeft: 6, fontSize: 11 }}>
                      (#{o.order_id})
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: "#334155" }}>{o.quantity} {o.unit || "pcs"}</span>
                    <span className="badge purple" style={{ fontSize: 10 }}>In Progress</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="dispatch-field">
            <label className="dispatch-label">Completion Date *</label>
            <input
              type="date"
              className="dispatch-control"
              value={actualDate}
              onChange={(e) => setActualDate(e.target.value)}
              required
              data-testid="batch-complete-date-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Completion Remarks</label>
            <input
              type="text"
              className="dispatch-control"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Printing and finishing completed on shop floor"
              data-testid="batch-complete-remarks-input"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#16a34a", borderColor: "#16a34a", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}
              disabled={submitting || eligibleOrders.length === 0}
              data-testid="confirm-batch-complete-btn"
            >
              <CheckCircle2 size={12} />
              <span>{submitting ? "Completing..." : `Confirm Complete All (${eligibleOrders.length})`}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Bulk Start Production Modal (for multi-selected standalone orders)
// -------------------------------------------------------------
function BulkStartOrdersModal({ selectedOrders, vendors, warehouses, onClose, onStarted }) {
  const eligibleOrders = useMemo(() => {
    return (selectedOrders || []).filter((o) => (o.production_status || "Not Started") === "Not Started");
  }, [selectedOrders]);

  const [selectedIds, setSelectedIds] = useState(eligibleOrders.map((o) => o.order_id));
  const [defaultVendorId, setDefaultVendorId] = useState("");
  const [defaultWarehouseId, setDefaultWarehouseId] = useState("");
  const [startDate, setStartDate] = useState(getTodayDateString());
  const [submitting, setSubmitting] = useState(false);

  const unassignedCount = useMemo(() => {
    return eligibleOrders.filter(
      (o) => !o.production_vendor_id && !o.production_vendor_name && !o.assigned_producer && !o.warehouse_id && !o.warehouse_name
    ).length;
  }, [eligibleOrders]);

  const toggleAll = () => {
    if (selectedIds.length === eligibleOrders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(eligibleOrders.map((o) => o.order_id));
    }
  };

  const toggleOne = (oid) => {
    if (selectedIds.includes(oid)) {
      setSelectedIds(selectedIds.filter((x) => x !== oid));
    } else {
      setSelectedIds([...selectedIds, oid]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) {
      toast.error("Please select at least one order to start.");
      return;
    }

    const v = vendors.find((x) => x.vendor_id === defaultVendorId);
    const w = warehouses.find((x) => x.warehouse_id === defaultWarehouseId);

    setSubmitting(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const oid of selectedIds) {
      const order = eligibleOrders.find((o) => o.order_id === oid);
      const isAssigned = Boolean(
        order?.production_vendor_id || order?.production_vendor_name || order?.assigned_producer || order?.warehouse_id || order?.warehouse_name
      );

      const payload = {
        status: "In Progress",
        start_date: startDate || getTodayDateString(),
        remarks: "Started via bulk quick start",
      };

      if (!isAssigned) {
        if (defaultVendorId) {
          payload.vendor_id = defaultVendorId;
          payload.vendor_name = v?.name || v?.vendor_name || "";
        } else if (defaultWarehouseId) {
          payload.warehouse_id = defaultWarehouseId;
          payload.warehouse_name = w?.name || w?.warehouse_name || "";
        }
      }

      try {
        await api("patch", `/orders/${oid}/production`, payload);
        successCount++;
      } catch (err) {
        failCount++;
        const msg = err.response?.data?.detail || "Error starting";
        errors.push(`#${oid}: ${msg}`);
      }
    }

    setSubmitting(false);
    if (successCount > 0) {
      toast.success(`Successfully started ${successCount} order(s) in production!`);
      onStarted(selectedIds);
    }
    if (failCount > 0) {
      toast.error(`Failed to start ${failCount} order(s). Make sure vendors/warehouses are assigned.`);
    }
    onClose();
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 580, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label">BULK START PRODUCTION</div>
            <h3>Start Production for Selected Orders</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          {unassignedCount > 0 && (
            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 6,
                padding: "10px 12px",
                marginBottom: 14,
                fontSize: 12,
                color: "#92400e",
              }}
            >
              <div style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 700 }}>
                <AlertCircle size={14} /> {unassignedCount} of {eligibleOrders.length} selected orders have no vendor or warehouse
              </div>
              <div style={{ marginTop: 4, color: "#78350f" }}>
                Select a default Vendor or Warehouse below to automatically assign them during start:
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                <select
                  className="dispatch-control"
                  value={defaultVendorId}
                  onChange={(e) => {
                    setDefaultVendorId(e.target.value);
                    if (e.target.value) setDefaultWarehouseId("");
                  }}
                  style={{ fontSize: 12 }}
                >
                  <option value="">-- Assign Default Vendor --</option>
                  {vendors.map((v) => (
                    <option key={v.vendor_id} value={v.vendor_id}>
                      {v.name || v.vendor_name}
                    </option>
                  ))}
                </select>
                <select
                  className="dispatch-control"
                  value={defaultWarehouseId}
                  onChange={(e) => {
                    setDefaultWarehouseId(e.target.value);
                    if (e.target.value) setDefaultVendorId("");
                  }}
                  style={{ fontSize: 12 }}
                >
                  <option value="">-- Assign Default Warehouse --</option>
                  {warehouses.map((w) => (
                    <option key={w.warehouse_id} value={w.warehouse_id}>
                      {w.name || w.warehouse_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="dispatch-field">
            <label className="dispatch-label">Production Start Date</label>
            <input
              type="date"
              className="dispatch-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label className="dispatch-label" style={{ margin: 0 }}>
                Eligible Orders ({selectedIds.length}/{eligibleOrders.length})
              </label>
              <button type="button" className="outline-btn compact" onClick={toggleAll} style={{ padding: "2px 6px", fontSize: 11 }}>
                {selectedIds.length === eligibleOrders.length ? "Deselect All" : "Select All"}
              </button>
            </div>

            <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 8px" }}>
              {eligibleOrders.map((o) => {
                const isAssigned = Boolean(
                  o.production_vendor_name || o.assigned_producer || o.warehouse_name
                );
                return (
                  <label
                    key={o.order_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "5px 6px",
                      fontSize: 12,
                      cursor: "pointer",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(o.order_id)}
                      onChange={() => toggleOne(o.order_id)}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span className="mono" style={{ color: "#2563eb", fontWeight: 700 }}>#{o.order_id}</span>
                        <strong style={{ color: "var(--navy)" }}>{o.client_name}</strong>
                      </div>
                      <div style={{ color: "#475569", fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.product} · {o.quantity} {o.unit || "units"}
                      </div>
                    </div>
                    {isAssigned ? (
                      <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>Ready</span>
                    ) : (
                      <span style={{ fontSize: 10.5, color: "#d97706", fontWeight: 600 }}>Needs Assignment</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#16a34a", borderColor: "#16a34a" }}
              disabled={submitting || selectedIds.length === 0}
              data-testid="bulk-confirm-start-btn"
            >
              <Play size={13} fill="currentColor" />
              {submitting ? "Starting..." : `Start Production (${selectedIds.length})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Put On Hold Modal
// -------------------------------------------------------------
function HoldProductionModal({ order, onClose, onConfirm }) {
  const [holdReason, setHoldReason] = useState(order.hold_reason || "");
  const [nextEstDate, setNextEstDate] = useState(order.estimated_ready_date || order.production_expected || "");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!holdReason.trim()) {
      toast.error("Please provide a reason for placing this order on hold.");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({
        orderId: order.order_id,
        holdReason: holdReason.trim(),
        nextEstDate: nextEstDate || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 480, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#d97706" }}>STATUS TRANSITION</div>
            <h3>Put Production On Hold</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          {/* Order Details Preview */}
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 16,
              fontSize: 12.5,
              color: "#92400e",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
              #{order.order_id} — {order.client_name}
            </div>
            <div>{order.product} · {order.quantity} {order.unit || "units"}</div>
            <div style={{ marginTop: 4, fontSize: 11.5, color: "#b45309" }}>
              Placing this order on hold will pause production and flag it for shop-floor review.
            </div>
          </div>

          <div className="dispatch-field">
            <label className="dispatch-label">Reason for Hold *</label>
            <textarea
              className="dispatch-control"
              rows={3}
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder="e.g. Awaiting client material confirmation, vendor dye shortage, proof clarification..."
              required
              autoFocus
              data-testid="hold-reason-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Revised Expected Ready Date (Optional)</label>
            <input
              type="date"
              className="dispatch-control"
              value={nextEstDate}
              onChange={(e) => setNextEstDate(e.target.value)}
              data-testid="hold-revised-date-input"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#d97706", borderColor: "#d97706", color: "#ffffff", display: "inline-flex", alignItems: "center", gap: 6 }}
              disabled={submitting || !holdReason.trim()}
              data-testid="confirm-put-on-hold-btn"
            >
              <Pause size={12} />
              <span>{submitting ? "Placing On Hold..." : "Confirm Put On Hold"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Complete Production Modal
// -------------------------------------------------------------
function CompleteProductionModal({ order, onClose, onConfirm }) {
  const [actualDate, setActualDate] = useState(getTodayDateString());
  const [remarks, setRemarks] = useState("Production completed directly from order list");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onConfirm({
        orderId: order.order_id,
        actualDate: actualDate || getTodayDateString(),
        remarks: remarks || "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 480, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#16a34a" }}>STATUS TRANSITION</div>
            <h3>Complete Production</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          {/* Order Details Preview */}
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 16,
              fontSize: 12.5,
              color: "#166534",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
              #{order.order_id} — {order.client_name}
            </div>
            <div>{order.product} · {order.quantity} {order.unit || "units"}</div>
            <div style={{ marginTop: 4, fontSize: 11.5, color: "#15803d" }}>
              Are you sure? Marking this order as completed will finish production and transition the order directly to <strong>Dispatch</strong>.
            </div>
          </div>

          <div className="dispatch-field">
            <label className="dispatch-label">Completion Date *</label>
            <input
              type="date"
              className="dispatch-control"
              value={actualDate}
              onChange={(e) => setActualDate(e.target.value)}
              required
              data-testid="complete-actual-date-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Completion Remarks (Optional)</label>
            <input
              type="text"
              className="dispatch-control"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Production passed QC inspection and packaged"
              data-testid="complete-remarks-input"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#16a34a", borderColor: "#16a34a", display: "inline-flex", alignItems: "center", gap: 6 }}
              disabled={submitting}
              data-testid="confirm-mark-complete-btn"
            >
              <CheckCircle2 size={13} />
              <span>{submitting ? "Completing..." : "Confirm & Move to Dispatch"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function MetricBox({ label, value, tone = "blue", onClick, active }) {
  return (
    <div
      className={`metric ${tone}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        ...(active ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {})
      }}
      title={onClick ? `Filter by ${label}` : undefined}
      data-testid={`production-metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="metric-top">
        <span>{label}</span>
        {active && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>FILTERED</span>}
      </div>
      <strong>{value}</strong>
    </div>
  );
}


// -------------------------------------------------------------
// Assign Production User Modal (Single & Batch)
// -------------------------------------------------------------
export function AssignProductionUserModal({ data, users = [], onClose, onSave }) {
  const { mode, order, batch, targetOrders = [] } = data || {};
  const isBatch = mode === "batch" || Boolean(batch);
  const targetList = targetOrders.length > 0 ? targetOrders : order ? [order] : [];

  // Initial selection
  const firstTarget = targetList[0] || {};
  const currentAssignedName = firstTarget.assigned_producer || firstTarget.assigned_production_user || "";
  const initialUser = users.find((u) => u.name === currentAssignedName || u.user_id === firstTarget.producer_id || u.user_id === firstTarget.production_user_id);

  const [selectedUserId, setSelectedUserId] = useState(initialUser ? initialUser.user_id : currentAssignedName ? "__custom__" : "");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let assignedUser = "";
      let assignedUserId = "";

      if (selectedUserId && selectedUserId !== "__unassign__") {
        const found = users.find((u) => u.user_id === selectedUserId);
        if (found) {
          assignedUser = found.name;
          assignedUserId = found.user_id;
        } else if (selectedUserId === "__custom__") {
          assignedUser = currentAssignedName;
          assignedUserId = firstTarget.producer_id || firstTarget.production_user_id || "";
        }
      }

      await onSave({
        mode,
        order,
        batch,
        targetOrders: targetList,
        assignedUser,
        assignedUserId,
        remarks: remarks.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 480, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#059669" }}>INTERNAL CRM RESPONSIBILITY</div>
            <h3>{isBatch ? `Assign Production User (Batch #${batch?.batch_id})` : `Assign Production User (#${order?.order_id})`}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} disabled={submitting}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          {/* Order / Batch Summary */}
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 16,
              fontSize: 12.5,
              color: "#166534",
            }}
          >
            {isBatch ? (
              <>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
                  Bulk Request #{batch?.batch_id} • {targetList.length} Order(s)
                </div>
                <div>Client: {batch?.client_name || firstTarget?.client_name}</div>
                <div style={{ marginTop: 4, fontSize: 11.5, color: "#15803d" }}>
                  The selected internal user will be assigned as responsible for all {targetList.length} products in this batch.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
                  #{order?.order_id} • {order?.product}
                </div>
                <div>Client: {order?.client_name} • {order?.quantity} {order?.unit || "units"}</div>
                {currentAssignedName && (
                  <div style={{ marginTop: 4, fontSize: 11.5, color: "#15803d" }}>
                    Currently assigned to: <b>{currentAssignedName}</b>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="dispatch-field">
            <label className="dispatch-label">Select Internal Production User *</label>
            <select
              className="dispatch-control"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              data-testid="select-production-user"
              required
            >
              <option value="">-- Choose Production User --</option>
              <option value="__unassign__">❌ Unassign / Remove User</option>
              {users.map((u) => (
                <option key={u.user_id || u.name} value={u.user_id}>
                  {u.name} ({u.role || u.department || "Staff"})
                </option>
              ))}
            </select>
            <small style={{ color: "#64748b", fontSize: 11, marginTop: 4, display: "block" }}>
              Only active users authorized for Production / Administration are listed.
            </small>
          </div>

          <div className="dispatch-field" style={{ marginTop: 14 }}>
            <label className="dispatch-label">Assignment Remarks / Instructions (Optional)</label>
            <textarea
              className="dispatch-control"
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Assigned to monitor print quality and vendor timeline"
              data-testid="assign-user-remarks"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#059669", borderColor: "#059669", display: "inline-flex", alignItems: "center", gap: 6 }}
              disabled={submitting || !selectedUserId}
              data-testid="confirm-assign-user-btn"
            >
              <UserCheck size={13} />
              <span>{submitting ? "Saving..." : selectedUserId === "__unassign__" ? "Confirm Unassign" : "Save Assignment"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
